/* istanbul ignore file */
import { AgTableServer, AgTableClient } from "app/shared/AgTable";
import { AgCharts } from "ag-charts-react";
import PageHeader from "app/shared/PageHeader";
import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { Select, Button, Modal, Input, message, DatePicker, Popover, Tag, Tooltip } from "antd"; // Import Ant Design components
import moment from "moment";

import { apiGet, apiPost } from "app/services/apiServices";
import * as XLSX from "xlsx";
import { API_ENDPOINT_FETCH_TEMPLATE_INFO } from "../HotelBookingView/hotel.booking.constants";
import { PlusOutlined, RightOutlined, LeftOutlined, UserOutlined, TeamOutlined } from "@ant-design/icons";
import Handlebars from "handlebars";
import "./HotelEmailFollowUp.css";
import Typography from "app/shared/Typography";
import HotelDataUpdate from "./HotelDataUpdateEmai";
import { Skeleton, Statistic } from "antd";
import { API_ENDPOINT_EMAIL_FOLLOWUP_CLIENT_NAME } from "global.constants";
import { API_ENDPOINT_FETCH_TMCLISTS, API_ENDPOINT_FETCH_TMCLISTS_DIRECT } from "app/scenes/FinkAnalytics/finkanalytics.constants";
import { useNavigate } from "react-router-dom";
import { getSubdomain } from "app/utils/generic.helper";
import { useRecoilState, useRecoilValue } from "recoil";
import { userInfo, activeTabAtom } from "app/config/States/users";
import { activeTableNameState } from "app/config/States/agtable";

import {
  AIRLINE_FOLLOWUP_COLUMN_DEFS,
  HOTEL_FOLLOWUP_COLUMN_DEFS,
  CLIENT_FOLLOWUP_COLUMN_DEFS,
} from "./email.follow.helpers";
import EmailActivityList from "../ActivityList";
import { Drawer } from "antd";
import { formatAmountIndian, convertFileToBase64 } from "./helpers";
import { filter } from "lodash";

const { Option } = Select;

const BCD_ACTIVITY_TYPE = {
  BOOKING_HOTEL: " 🎉 Congratulation, you have booking for stay ",
  TO_TRAVELLER_PRIOR_CHECKIN: " You are about to checkin tomorrow ",
  TO_TRAVELLER_ON_CHECKIN_DAY: " You are about to checkin today ",
  CHECKIN_HOTEL: " Hey!, Your guest has arrived at your hotel ",
  TO_TRAVELLER_BEFORE_CHECKOUT: " You are about to checkout today ",
  MAIL_TO_TRAVELLER: " Hurry Up!!, Your guest is about to check-out in next 5 hour ",
  BEFORE_CHECKOUT_HOTEL: " Hurry Up!!, Your guest is about to check-out in next 2 hours ",
  AFTER_CHECKOUT_HOTEL: "You are late!!, Your guest has checked out already ",
};

const DEFAULT_COL_DEF = {
  filter: true,
  floatingFilter: true,
  sortable: true,
  resizable: true,
  enableRowGroup: true,
  enablePivot: true,
  enableValue: true,
  cellStyle: { textAlign: "center" },
  headerClass: "header-center",
};


const CUSTOMER_INFO_COLUMN_DEFS = [
  {
    field: "customer_code",
    headerName: "Customer Code",
    flex: 1,
    minWidth: 140,
    filter: "agTextColumnFilter",
    enableRowGroup: true,


    valueGetter: (params) => {
      return String(params.data?.customer_code || "");
    },

    cellRenderer: (params) => {
      return <Tag>{params.value}</Tag>;
    },
  },
  {
    field: "name",
    headerName: "Name",
    flex: 2,
    minWidth: 250,
    formatType: "ENTITY",
    filter: "agTextColumnFilter",
    enableRowGroup: true,
  },
  {
    field: "gstin",
    headerName: "GSTIN",
    flex: 1,
    minWidth: 160,
    formatType: "CHIP",
    filter: "agTextColumnFilter",
    enableRowGroup: true,
  },
  {
    field: "pan",
    headerName: "PAN",
    flex: 1,
    minWidth: 130,
    formatType: "CHIP",
    filter: "agTextColumnFilter",
    enableRowGroup: true,
  },
  {
    field: "stateCd",
    headerName: "State Code",
    flex: 1,
    minWidth: 110,
    formatType: "GSTIN_STATE",
    enableRowGroup: true,
  },


  {
    field: "authStatus",
    headerName: "Auth Status",
    flex: 1,
    minWidth: 120,

    cellRenderer: (params) => {
      if (params.value === "Active") {
        return (
          <Tag color="green" style={{ fontSize: "11px", padding: "0px 6px", lineHeight: "18px" }}>
            {params.value}
          </Tag>
        );
      }
      if (params.value === "Inactive") {
        return (
          <Tag color="red" style={{ fontSize: "11px", padding: "0px 6px", lineHeight: "18px" }}>
            {params.value}
          </Tag>
        );
      }
      return params.value || "-";
    },
  },
  { field: "taxpayertype", headerName: "Taxpayer Type", flex: 1, minWidth: 140, enableRowGroup: true, },
  { field: "gstin_address", headerName: "GSTIN Address", flex: 3, minWidth: 250, enableRowGroup: true, },
];

const HotelEmail = () => {
  const { TextArea } = Input;
  const [userDetails] = useRecoilState(userInfo);
  const activeTab = useRecoilValue(activeTabAtom);
  const activeTableName = useRecoilValue(activeTableNameState);
  const domain = getSubdomain();
  const isBcd = domain?.toLowerCase() === "bcd";
  const email = userDetails?.email || "";
  const isDevApp = domain?.toLowerCase() === "app" && email === "developer@finkraft.ai";
  const isNewUIDomain = isBcd || domain?.toLowerCase() === "app";
  const hotelFollowUpEndpoint = isNewUIDomain
    ? "https://stage-apiplatform.finkraftai.com/api/hotelFollowUp/data"
    : "https://stage-apiplatform.finkraftai.com/api/hotelFollowUp/booking";
  const workspaceName = userDetails?.currentWorkspace?.name;
  const userEmail = email;
  const isInternalUser =
    userEmail.includes("finkraft.ai") || userEmail.includes("kgrp.in");
  // Hide UI only if: not internal user AND domain is "app" AND workspace is "Haleon"
  const shouldHideUI =
    !isInternalUser && domain === "app" && workspaceName === "Haleon";

  const [selectedClient, setSelectedClient] = useState("");
  const [dateFilter, setDateFilter] = useState("");
  const [activeDatePreset, setActiveDatePreset] = useState("");
  const [selectedHotelName, setSelectedHotelName] = useState("");
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  // const [isEditingPincode, setIsEditingPincode] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [modalData, setModalData] = useState(null);
  const [contactDetails, setContactDetails] = useState(null);
  const [segmentType, setSegmentType] = useState("HOTEL");
  const [hotelFollowUpType, setHotelFollowUpType] = useState(isBcd ? "GSTIN_TRANSMISSION" : "INVOICE_FOLLOWUP");
  const [modalConfig, setModalConfig] = useState({ visible: false, type: "" });
  const [clientNames, setClientNames] = useState([]);
  const [clientNamesLoading, setClientNamesLoading] = useState(true);
  // const [columnDefs, setColumnDefs] = useState([]);
  // const [templateDataBulk, setTemplateDataBulk] = useState(null);

  const [pendingUpdates, setPendingUpdates] = useState([]);
  const [addConfigModalOpen, setAddConfigModalOpen] = useState(false);
  const [configClientType, setConfigClientType] = useState(null);
  const [configSelectedWorkspace, setConfigSelectedWorkspace] = useState(null);
  const [configTmcList, setConfigTmcList] = useState([]);
  const [configDirectList, setConfigDirectList] = useState([]);
  const [configListLoading, setConfigListLoading] = useState(false);
  const [gridApi, setGridApi] = useState(null);
  const agGridRef = useRef(null);
  const [eventData, setEventData] = useState(null);
  const [bcdWidgetCounts, setBcdWidgetCounts] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedEmailStatus, setSelectedEmailStatus] = useState("");
  const [selectedStatusLabel, setSelectedStatusLabel] = useState(null);
  const [isBulkMailModalVisible, setIsBulkMailModalVisible] = useState(false);
  const [isBulkSingleMailModalVisible, setIsBulkSingleMailModalVisible] = useState(false);
  const [selectedRowCount, setSelectedRowCount] = useState(0);
  const [bulkSingleSending, setBulkSingleSending] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [showTextarea, setShowTextarea] = useState(false);
  const [customMessage, setCustomMessage] = useState("");
  // New state for single mail modal
  const [isResendModalVisible, setIsResendModalVisible] = useState(false);
  const [resendRowData, setResendRowData] = useState(null);

  // 1. Add state for CC and multi-checkbox sender selection
  const [ccEmails, setCcEmails] = useState([]);

  // Add state for single mail CC and sender multi-select
  const [ccEmailsSingle, setCcEmailsSingle] = useState([]);
  const [refreshTrigger, setRefreshTrigger] = useState(0); // Add refresh trigger
  const [open, setOpen] = useState(false);
  const [customerInfoData, setCustomerInfoData] = useState([]);
  const [isCustomerInfoModalVisible, setIsCustomerInfoModalVisible] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState("DEFAULT");
  // const isBcd = domain?.toLowerCase() === "bcd";

  // // then only compute the new-UI values when needed:
  // const sent = isBcd ? (eventData?.summary?.delivered ?? 0) : 0;
  // // …or guard the whole block:
  // const newCardData = isBcd ? { /* all the computed values + chart options */ } : null;

  const toggleDrawer = () => {
    setOpen((prev) => !prev);
  };

  const clientTemplateHtmlMapRef = useRef({});
  const clientNamesMapRef = useRef({});
  const [nocFile, setNocFile] = useState([]);

  // Handler to open single mail modal
  const openResendModal = useCallback((rowData) => {
    setResendRowData(rowData);
    setIsResendModalVisible(true);
  }, []);
  // Handle Client Selection Change
  const handleChange = (value) => {
    if (Array.isArray(value) && value.includes("__all__")) {
      const filteredIds = (clientNames || [])
        .filter((item) => !segmentType || item.type?.toUpperCase() === segmentType)
        .map((item) => item.client_id);
      const alreadyAllSelected =
        Array.isArray(selectedClient) && filteredIds.every((id) => selectedClient.includes(id));
      setSelectedClient(alreadyAllSelected ? [] : filteredIds);
    } else {
      setSelectedClient(value);
    }
  };
  const controllerRef = useRef(null);

  const fetchEventSummary = async (clientId, segmentType) => {
    if (controllerRef.current) {
      controllerRef.current.abort();
    }

    const controller = new AbortController();
    controllerRef.current = controller;
    setLoading(true); // ✅ start loading
    setEventData(null); // show skeleton

    try {
      const response = await fetch(
        `https://stage-apiplatform.finkraftai.com/api/hotelFollowUp/event_summary`,
        //`http://192.168.0.193:7002/api/hotelFollowUp/event_summary`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({
            booking_client_id: clientId || "ALL",
            // segmentType: segmentType || "ALL"
            segmentType: segmentType
              ? segmentType.charAt(0).toUpperCase() +
              segmentType.slice(1).toLowerCase()
              : "ALL",
          }),
          signal: controller.signal,
        },
      );

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();
      setEventData(data);
    } catch (error) {
      void error;
    } finally {
      setLoading(false); // hide skeleton
    }
  };

  useEffect(() => {
    if (isNewUIDomain) return; // new-UI domains get widgetCounts + eventData from onResponseData (SSR response)
    const clientId = Array.isArray(selectedClient)
      ? selectedClient.join(",")
      : selectedClient;
    fetchEventSummary(clientId, segmentType);
  }, [selectedClient, segmentType]);

  // Sync selectedStatusLabel with selectedEmailStatus
  useEffect(() => {
    if (!selectedEmailStatus) {
      setSelectedStatusLabel("");
    }
  }, [selectedEmailStatus]);


  const handleOk = () => {
    setIsModalVisible(false);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
  };

  const openEmailModal = useCallback((data) => {
    setContactDetails(data);
    setModalConfig({ visible: true, type: "email" });
  }, []);

  const openPhoneModal = useCallback((data) => {
    setContactDetails(data);
    setModalConfig({ visible: true, type: "phone" });
  }, []);
  // removed unused handleEmailSubmit

  // useEffect(() => {
  //   fetchClientNames();
  // }, [segmentType]);
  useEffect(() => {
    fetchClientNames();
  }, [segmentType, workspaceName]);

  useEffect(() => {
    if (hotelFollowUpType) {
      fetchClientNames();
    }
  }, [hotelFollowUpType]);

  const fetchTemplateInfo = async (clientId, templateId) => {
    const response = await apiGet(
      API_ENDPOINT_FETCH_TEMPLATE_INFO + templateId,
    );
    if (response?.html_content) {
      clientTemplateHtmlMapRef.current[`${clientId}_${templateId}`] = {
        html_content: response.html_content,
        client_id: clientId,
        template_id: templateId,
      };
    }
  };


  const renderTemplate = (htmlContent, data) => {
    const templateCompiler = Handlebars.compile(htmlContent);
    const renderedContent = templateCompiler(data);
    return renderedContent;
  };

  //--new fun

  const handleCellValueChange = useCallback((params) => {
    const { field } = params.colDef;
    const updatedValue = params.newValue;
    const rowData = params.data;

    const allowedFields = [
      "pincode",
      "email_hotel_name",
      "email_hotel_address",
      "email_hotel_gstin",
      "email_hotel_city",
      "remark1",
      "remark2",
    ];

    if (allowedFields.includes(field)) {
      // ✅ Check if this row is checkbox-selected
      const gridApi = agGridRef.current?.api;
      if (!gridApi) return;

      const selectedNodes = gridApi.getSelectedNodes();
      const isRowSelected = selectedNodes.some(
        (node) =>
          node.data &&
          (node.data.email_bookingId === rowData.email_bookingId ||
            node.data._id === rowData._id),
      );

      if (!isRowSelected) {
        return;
      }

      const rowId = rowData.email_bookingId || rowData._id;

      // ✅ Update editedRows Map to track multiple rows
      setEditedRows((prev) => {
        const newMap = new Map(prev);
        const existingRow = newMap.get(rowId) || { ...rowData };
        newMap.set(rowId, { ...existingRow, [field]: updatedValue });
        return newMap;
      });

      // ✅ Set edited pincode separately if relevant (removed)

      // ✅ Always show save button when any row is edited
      const saveBtn = document.getElementById("saveBtn");
      if (saveBtn) saveBtn.style.display = "block";
    }

    // ✅ Maintain pendingUpdates
    if (allowedFields.includes(field)) {
      setPendingUpdates((prev) => {
        const existing = prev.find(
          (item) => item.email_bookingId === rowData.email_bookingId,
        );

        if (existing) {
          return prev.map((item) =>
            item.email_bookingId === rowData.email_bookingId
              ? { ...item, [field]: updatedValue }
              : item,
          );
        } else {
          return [
            ...prev,
            { email_bookingId: rowData.email_bookingId, [field]: updatedValue },
          ];
        }
      });
    }
  }, []);

  const handleUpdateClick = async () => {
    try {
      for (let update of pendingUpdates) {
        const response = await fetch(
          "https://stage-apiplatform.finkraftai.com/api/hotelFollowUp/cellDetailsUpdate",
          // "http://192.168.0.174:7002/api/hotelFollowUp/cellDetailsUpdate",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify(update),
          },
        );

        if (!response.ok) throw new Error("Failed to update");
      }

      setPendingUpdates([]);
    } catch (error) {
      void error;
    }
  };


  const fetchCustomerInfo = async () => {
    const clientIds = Array.isArray(selectedClient)
      ? selectedClient
      : selectedClient
        ? [selectedClient]
        : [];
    try {
      const res = await fetch("https://stage-apiplatform.finkraftai.com/api/hotelFollowUp/customerDetails", {
        //const res = await fetch("https://stage-apiplatform.finkraftai.com/api/hotelFollowUp/customerDetails", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ client_id: clientIds }),
      });
      const response = await res.json();
      if (response.success || response.status) {
        const data = response.data;
        setCustomerInfoData(Array.isArray(data) ? data : data ? [data] : []);
      }
    } catch (e) {
      setCustomerInfoData([]);
    }
  };
  // Column Definitions for Ag-Grid



  const fetchClientNames = async () => {
    try {
      setClientNamesLoading(true);
      setSelectedClient("");
      setClientNames([]);
      setBcdWidgetCounts(null);
      setEventData(null);

      const clientNamesPayload = {
        type:
          segmentType.charAt(0).toUpperCase() +
          segmentType.slice(1).toLowerCase(),
        domain: domain,
        userDetails: {
          email: userDetails?.email,
          currentWorkspace: userDetails?.currentWorkspace?.name,
          workspaceId: userDetails?.currentWorkspace?.id,
        },
      };

      if (isDevApp && workspaceName) {
        clientNamesPayload.workspace = workspaceName;
      }

      if (hotelFollowUpType) {
        clientNamesPayload.serviceType = hotelFollowUpType;
      }

      const res = await fetch(
        "https://stage-apiplatform.finkraftai.com/api/hotelFollowUp/clientnames",
        // "http://192.168.0.13:7002/api/hotelFollowUp/clientnames",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(clientNamesPayload),
        }
      );

      const response = await res.json();

      if (response.status) {
        setClientNames(response.data);

        clientNamesMapRef.current = Object.fromEntries(
          response.data.map((c) => [c.client_id, c])
        );

        const allIds = (response.data || []).map(
          (c) => c.client_id
        );

        if (allIds.length > 0) {
          setSelectedClient(allIds);
        }

        response.data.forEach((client) => {
          if (Array.isArray(client.templates)) {
            client.templates.forEach((tmpl) => {
              fetchTemplateInfo(
                client.client_id,
                tmpl.templateId
              );
            });
          }
        });
      }
    } catch (error) {
      console.error("Error fetching client names:", error);
    } finally {
      setClientNamesLoading(false);
    }
  };

  const serverPayload = useMemo(() => {
    const payload = {
      database: "unified-hotel-followup",
      table: "booking-data",
      domain: domain,
      selectedClient: Array.isArray(selectedClient)
        ? selectedClient.join(",")
        : selectedClient,
      // type:segmentType,
      type: segmentType
        ? segmentType.charAt(0).toUpperCase() +
        segmentType.slice(1).toLowerCase()
        : segmentType,

      email_status: selectedEmailStatus,
      refreshTrigger: refreshTrigger,
    };

    if (dateFilter) payload.dateFilter = dateFilter;
    if (selectedHotelName) payload.hotelName = selectedHotelName;

    if (shouldHideUI) {
      payload.booking_client_id = "HALEON_HOTEL_GSTIN_TRANSMISSION";
    }

    if (isDevApp) {
      if (workspaceName) payload.workspace = workspaceName;
      if (selectedClient) {
        const clientObj = clientNames.find((c) => c.client_id === selectedClient);
        payload.clientName = clientObj?.client_name || selectedClient;
      }
    }

    return payload;
  }, [
    selectedClient,
    selectedEmailStatus,
    refreshTrigger,
    shouldHideUI,
    segmentType,
    dateFilter,
    selectedHotelName,
    isDevApp,
    workspaceName,
    domain,
    clientNames,
  ]);

  const dateFilterLabel = useMemo(() => {
    if (!dateFilter) return "Select date range";
    if (dateFilter.includes(",")) {
      const [s, e] = dateFilter.split(",");
      return `${moment(s).format("DD MMM YYYY")}  →  ${moment(e).format("DD MMM YYYY")}`;
    }
    return moment(dateFilter).format("DD MMM YYYY");
  }, [dateFilter]);

  const navigate = useNavigate();

  const handleGridReady = (params) => {
    setGridApi(params.api);

    setTimeout(() => {
      params.api.forEachNode((node) => {
        if (node.group) node.setExpanded(true);
      });
    }, 500);

    // Auto-filter booking_client_id to "Haleon" when workspace is Haleon and user is not internal
    if (shouldHideUI && agGridRef.current) {
      setTimeout(() => {
        agGridRef.current.setFilterModel({
          booking_client_id: {
            type: "contains",
            filter: "Haleon",
          },
        });
      }, 600);
    }
  };

  // Function to refresh grid data from server
  const refreshGridData = useCallback(() => {
    // Method 1: Try AgTableServer ref approach
    if (agGridRef && agGridRef.current) {
      const agTableComponent = agGridRef.current;

      // Access the internal grid API
      if (agTableComponent.api) {
        const api = agTableComponent.api;
        if (api.refreshServerSideStore) {
          api.refreshServerSideStore({ purge: true });
        } else if (api.refreshServerSide) {
          api.refreshServerSide();
        }
      }

      // Try to access AgTableServer's internal fetchServerSide method
      if (agTableComponent.fetchServerSide) {
        agTableComponent.fetchServerSide();
      }
    }

    // Method 2: Direct gridApi approach
    if (gridApi) {
      if (gridApi.refreshServerSideStore) {
        gridApi.refreshServerSideStore({ purge: true });
      } else if (gridApi.refreshServerSide) {
        gridApi.refreshServerSide();
      }
    }

    // Method 3: Force complete refresh with delay
    setTimeout(() => {
      if (gridApi && gridApi.refreshServerSideStore) {
        gridApi.refreshServerSideStore({ purge: true });
      }
    }, 100);

    // Method 4: Trigger refresh by updating state
    setRefreshTrigger((prev) => prev + 1);
  }, [gridApi, agGridRef]);

  // Effect to trigger refresh when refreshTrigger changes
  useEffect(() => {
    if (refreshTrigger > 0 && gridApi) {
      setTimeout(() => {
        if (gridApi.refreshServerSideStore) {
          gridApi.refreshServerSideStore({ purge: true });
        }
      }, 200);
    }
  }, [refreshTrigger, gridApi]);

  // Auto-filter booking_client_id to "Haleon" when workspace is Haleon and user is not internal
  useEffect(() => {
    if (shouldHideUI && agGridRef.current) {
      setTimeout(() => {
        agGridRef.current.setFilterModel({
          booking_client_id: {
            type: "contains",
            filter: "Haleon",
          },
        });
      }, 300);
    }
  }, [shouldHideUI]);

  // Effect to handle cell update trigger
  // removed unused cellUpdateTrigger effect

  // Function to update row data - use minimal server-side refresh
  const updateRowData = useCallback(
    (_updatedRowData) => {
      // For AgTableServer with server-side data source, we need to refresh
      // But we can make it as minimal as possible

      if (gridApi) {
        // Use the most efficient refresh method
        if (gridApi.refreshServerSideStore) {
          gridApi.refreshServerSideStore({ purge: true });
        } else if (gridApi.refreshServerSide) {
          gridApi.refreshServerSide();
        }
      }

      // Also try using the AgTableServer ref
      if (agGridRef && agGridRef.current) {
        const agTableComponent = agGridRef.current;

        if (agTableComponent.api) {
          const api = agTableComponent.api;
          if (api.refreshServerSideStore) {
            api.refreshServerSideStore({ purge: true });
          } else if (api.refreshServerSide) {
            api.refreshServerSide();
          }
        }
      }
    },
    [gridApi, agGridRef],
  );

  const handleFirstDataRendered = (params) => {
    if (segmentType === "AIRLINE") {
      params.columnApi.setRowGroupColumns([
        "followup_completed",
        "company_name",
        "airline_name",
      ]);
    }
  };

  // const columnDefs = useMemo(() => {
  //   return segmentType === "HOTEL"
  //     ? HOTEL_FOLLOWUP_COLUMN_DEFS({
  //         setModalType,
  //         setModalData,
  //         setIsModalVisible,
  //       })
  //     : AIRLINE_FOLLOWUP_COLUMN_DEFS({
  //         setModalType,
  //         setModalData,
  //         setIsModalVisible,
  //       });
  // }, [segmentType, setModalType, setModalData, setIsModalVisible]);

  // Helper to check if any rows are selected (unused)

  // formatAmountIndian moved to module scope

  const StatusCard = ({
    label,
    value,
    icon,
    type,
    onClick,
    isSelected,
    customStyle,
    gst,
  }) => {
    const isFailure = type === "failure";
    let borderStyle = "none";
    if (isSelected) {
      borderStyle = isFailure ? "2px solid #E53935" : "2px solid #0A8394";
    }

    // Special rendering for Invoice Received/Not Received
    if (
      ["Invoice Received", "Invoice Not Received", "Email Required"].includes(
        label,
      )
    ) {
      return (
        <div
          onClick={onClick}
          style={{
            cursor: "pointer",
            border: borderStyle,
            borderRadius: 8,
            boxSizing: "border-box",
            ...customStyle,
            width: 180,
            minWidth: 204,
            // maxWidth: 180,
            height: 113,
            // minHeight: 120,
            // maxHeight: 120,
            // background: "#f8fcfd",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            margin: "0 8px",
          }}
          className={isFailure ? "bouncedContainer" : "deliveredContainer"}
        >
          <Typography
            className={isFailure ? "bouncedTxt" : "deliveredTxt"}
            style={{ fontSize: 16, color: "#666", fontWeight: 500 }}
          >
            {label}
          </Typography>
          <div
            style={{
              fontSize: 18,
              fontWeight: 700,
              color: isFailure ? "#E53935" : "#0A8394",
              marginTop: 4,
              display: "flex",
              alignItems: "center",
            }}
          >
            <span style={{ fontSize: 14, marginRight: 2 }}>₹</span>
            {formatAmountIndian(gst)}
          </div>
          <div style={{ fontSize: 13, color: "#888", marginTop: 2 }}>
            Count:{" "}
            <span
              style={{
                color: isFailure ? "#E53935" : "#0A8394",
                fontWeight: 600,
              }}
            >
              {value?.toLocaleString?.() ?? value}
            </span>
          </div>
        </div>
      );
    }

    return (
      <div
        onClick={onClick}
        style={{
          cursor: "pointer",
          border: borderStyle,
          borderRadius: 8,
          boxSizing: "border-box",
          ...customStyle,
        }}
        className={isFailure ? "bouncedContainer" : "deliveredContainer"}
      >
        <div className={isFailure ? "iconBounced" : "iconDelivered"}>
          <span>{icon}</span>
          <Typography className={isFailure ? "bouncedTxt" : "deliveredTxt"}>
            {label}
          </Typography>
        </div>
        <div
          className={isFailure ? "bouncedCount" : "deliveredCount"}
          style={{ maxWidth: 80 }}
        >
          {gst !== undefined && gst !== null && (
            <div
              style={{
                fontSize: "25px",
                fontWeight: 700,
                color: isFailure ? "#E53935" : "#0A8394",
                marginTop: 4,
                whiteSpace: "nowrap",
                display: "flex",
                alignItems: "center",
                gap: 2,
              }}
            >
              <span
                style={{
                  fontSize: "15px",
                  fontWeight: 700,
                  marginRight: 2,
                  display: "inline-block",
                }}
              >
                ₹
              </span>
              {formatAmountIndian(gst)}
            </div>
          )}
          {["Invoice Received", "Invoice Not Received"].includes(label) ? (
            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <span
                style={{ fontSize: "12px", color: "#666", fontWeight: 500 }}
              >
                Count:
              </span>
              <span
                style={{
                  fontSize: "18px",
                  fontWeight: 500,
                  lineHeight: "24px",
                  color: isFailure ? "#E53935" : "#0A8394",
                }}
              >
                {value?.toLocaleString?.() ?? value}
              </span>
            </div>
          ) : (
            <Statistic
              value={value}
              valueStyle={{
                fontSize: "18px", // reduced font size
                fontWeight: 500,
                lineHeight: "24px",
                color: isFailure ? "#E53935" : "#0A8394",
              }}
            />
          )}
        </div>
      </div>
    );
  };

  const summaryCards = [
    {
      label: "Delivered",
      value: eventData?.summary?.delivered,
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          fill="none"
          viewBox="0 0 24 24"
        >
          <path
            d="M6 3C5.5 3 5.1 3.2 4.8 3.6L3.5 5.2C3.2 5.6 3 6 3 6.5V19C3 20.1 3.9 21 5 21H13.3C13.1 20.4 13 19.7 13 19C13 15.7 15.7 13 19 13C19.7 13 20.4 13.1 21 13.3V6.5C21 6 20.8 5.6 20.5 5.2L19.1 3.5C18.9 3.2 18.5 3 18 3H6ZM5.9 4H17.9L18.8 5H5.1L5.9 4ZM6 15H12V18H6V15ZM21.3 15.8L17.7 19.4L16.1 17.8L15 19L17.8 22L22.6 17.2L21.3 15.8Z"
            fill="#0A8394"
          />
        </svg>
      ),
      type: "success",
    },
    {
      label: "Open",
      value: eventData?.summary?.open,
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          fill="none"
          viewBox="0 0 24 24"
        >
          <path
            d="M14.872 14.2872L21.394 20.8072C21.1324 21.154 20.7985 21.4399 20.4156 21.645C20.0326 21.8501 19.6097 21.9696 19.176 21.9952L19 22.0002H5C4.53604 22.0003 4.07837 21.8928 3.66297 21.6862C3.24757 21.4795 2.88577 21.1793 2.606 20.8092L9.127 14.2872L11.445 15.8322L11.561 15.8982C11.6977 15.965 11.8478 15.9997 12 15.9997C12.1522 15.9997 12.3023 15.965 12.439 15.8982L12.555 15.8322L14.872 14.2872Z"
            fill="#0A8394"
          />
          <path
            d="M2 9.53521L7.429 13.1552L2 18.5852V9.53521Z"
            fill="#0A8394"
          />
          <path
            d="M22 9.53521V18.5852L16.57 13.1552L22 9.53521Z"
            fill="#0A8394"
          />
          <path
            d="M12.44 2.10221L12.555 2.16821L20.999 7.79721L12 13.7972L3 7.79721L11.445 2.16721C11.5906 2.07045 11.7589 2.01354 11.9333 2.00213C12.1077 1.99073 12.2831 2.02523 12.44 2.10221Z"
            fill="#0A8394"
          />
        </svg>
      ),
      type: "success",
    },
    {
      label: "Click",
      value: eventData?.summary?.click,
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
        >
          <path
            d="M9.5 1.99979C10.1347 1.99969 10.7457 2.24102 11.209 2.67483C11.6724 3.10863 11.9534 3.70244 11.995 4.33579L12 4.49979V9.10479L17.442 9.70979C18.3855 9.81464 19.261 10.2516 19.9119 10.9427C20.5628 11.6337 20.9467 12.5337 20.995 13.4818L21 13.6848V13.9998C21 16.0782 20.1911 18.075 18.7445 19.5675C17.298 21.0599 15.3274 21.9308 13.25 21.9958L13 21.9998H12.326C10.8885 21.9998 9.47751 21.6124 8.24148 20.8784C7.00546 20.1444 5.99004 19.091 5.302 17.8288L5.171 17.5778L2.329 11.8938C1.969 11.1738 2.236 10.2108 3.076 9.86579C4.119 9.43879 5.11 9.35879 6.131 9.87779C6.353 9.99112 6.571 10.1291 6.785 10.2918L7 10.4618V4.49979C7 3.83675 7.26339 3.20086 7.73223 2.73202C8.20107 2.26318 8.83696 1.99979 9.5 1.99979ZM4 5.99979C4.26522 5.99979 4.51957 6.10514 4.70711 6.29268C4.89464 6.48022 5 6.73457 5 6.99979C5 7.265 4.89464 7.51936 4.70711 7.70689C4.51957 7.89443 4.26522 7.99979 4 7.99979H3C2.73478 7.99979 2.48043 7.89443 2.29289 7.70689C2.10536 7.51936 2 7.265 2 6.99979C2 6.73457 2.10536 6.48022 2.29289 6.29268C2.48043 6.10514 2.73478 5.99979 3 5.99979H4ZM16 4.99979C16.2549 5.00007 16.5 5.09767 16.6854 5.27263C16.8707 5.4476 16.9822 5.68674 16.9972 5.94118C17.0121 6.19562 16.9293 6.44617 16.7657 6.64162C16.6021 6.83707 16.3701 6.96268 16.117 6.99279L16 6.99979H15C14.7451 6.9995 14.5 6.90191 14.3146 6.72694C14.1293 6.55197 14.0178 6.31283 14.0028 6.05839C13.9879 5.80395 14.0707 5.55341 14.2343 5.35795C14.3979 5.1625 14.6299 5.03689 14.883 5.00679L15 4.99979H16ZM4.707 1.29279L5.707 2.29279C5.88916 2.48139 5.98995 2.73399 5.98767 2.99619C5.9854 3.25838 5.88023 3.5092 5.69482 3.6946C5.50941 3.88001 5.2586 3.98518 4.9964 3.98746C4.7342 3.98974 4.4816 3.88894 4.293 3.70679L3.293 2.70679C3.11084 2.51818 3.01005 2.26558 3.01233 2.00339C3.0146 1.74119 3.11977 1.49038 3.30518 1.30497C3.49059 1.11956 3.7414 1.01439 4.0036 1.01211C4.2658 1.00983 4.5184 1.11063 4.707 1.29279ZM15.707 1.29279C15.8945 1.48031 15.9998 1.73462 15.9998 1.99979C15.9998 2.26495 15.8945 2.51926 15.707 2.70679L14.707 3.70679C14.6148 3.8023 14.5044 3.87848 14.3824 3.93089C14.2604 3.9833 14.1292 4.01088 13.9964 4.01204C13.8636 4.01319 13.7319 3.98789 13.609 3.93761C13.4861 3.88733 13.3745 3.81307 13.2806 3.71918C13.1867 3.62529 13.1125 3.51364 13.0622 3.39074C13.0119 3.26784 12.9866 3.13616 12.9877 3.00339C12.9889 2.87061 13.0165 2.73939 13.0689 2.61738C13.1213 2.49538 13.1975 2.38503 13.293 2.29279L14.293 1.29279C14.4805 1.10532 14.7348 1 15 1C15.2652 1 15.5195 1.10532 15.707 1.29279Z"
            fill="#0A8394"
          />
        </svg>
      ),
      type: "success",
    },
    {
      label: "Bounce",
      value: eventData?.summary?.bounce,
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
        >
          <path
            d="M14.143 11.486C14.2842 11.2691 14.504 11.1154 14.7562 11.0572C15.0085 10.999 15.2734 11.0409 15.4954 11.174C15.7174 11.3071 15.8791 11.5211 15.9465 11.771C16.014 12.021 15.9819 12.2873 15.857 12.514C14.355 15.019 13.447 17.404 12.987 20.164C12.827 21.12 11.539 21.314 11.106 20.447C9.04598 16.327 7.24798 15.471 4.31599 16.449C4.19136 16.4905 4.05978 16.507 3.92876 16.4977C3.79775 16.4883 3.66985 16.4533 3.55239 16.3945C3.31515 16.2758 3.13479 16.0677 3.05099 15.816C2.96718 15.5643 2.98678 15.2896 3.1055 15.0524C3.22421 14.8152 3.43229 14.6348 3.68399 14.551C6.88399 13.484 9.33998 14.178 11.487 17.174L11.578 17.304L11.589 17.264C12.111 15.436 12.856 13.714 13.862 11.964L14.143 11.486ZM18 4C17.606 4 17.2159 4.0776 16.8519 4.22836C16.488 4.37913 16.1572 4.6001 15.8787 4.87868C15.6001 5.15726 15.3791 5.48797 15.2283 5.85195C15.0776 6.21593 15 6.60603 15 7C15 7.39397 15.0776 7.78407 15.2283 8.14805C15.3791 8.51203 15.6001 8.84274 15.8787 9.12132C16.1572 9.3999 16.488 9.62087 16.8519 9.77164C17.2159 9.9224 17.606 10 18 10C18.7956 10 19.5587 9.68393 20.1213 9.12132C20.6839 8.55871 21 7.79565 21 7C21 6.20435 20.6839 5.44129 20.1213 4.87868C19.5587 4.31607 18.7956 4 18 4Z"
            fill="#E53935"
          />
        </svg>
      ),
      type: "failure",
    },
    {
      label: "Dropped",
      value: eventData?.summary?.dropped,
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          fill="none"
          viewBox="0 0 24 24"
        >
          <path d="M9 4H15V12H19.84L12 19.84L4.16 12H9V4Z" fill="#E53935" />
        </svg>
      ),
      type: "failure",
    },
    {
      label: "Deferred",
      value: eventData?.summary?.deferred,
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
        >
          <path
            d="M17.5 12C18.9587 12 20.3576 12.5795 21.3891 13.6109C22.4205 14.6424 23 16.0413 23 17.5C23 18.9587 22.4205 20.3576 21.3891 21.3891C20.3576 22.4205 18.9587 23 17.5 23C16.0413 23 14.6424 22.4205 13.6109 21.3891C12.5795 20.3576 12 18.9587 12 17.5C12 16.0413 12.5795 14.6424 13.6109 13.6109C14.6424 12.5795 16.0413 12 17.5 12ZM15.024 15.024C14.9303 15.1178 14.8776 15.2449 14.8776 15.3775C14.8776 15.5101 14.9303 15.6372 15.024 15.731L16.793 17.501L15.026 19.267C14.9795 19.3134 14.9426 19.3685 14.9174 19.4292C14.8923 19.4899 14.8793 19.5549 14.8792 19.6206C14.8792 19.6863 14.8921 19.7514 14.9172 19.8121C14.9423 19.8728 14.9791 19.928 15.0255 19.9745C15.0719 20.021 15.127 20.0579 15.1877 20.0831C15.2484 20.1082 15.3134 20.1212 15.3791 20.1213C15.4448 20.1213 15.5099 20.1084 15.5706 20.0833C15.6313 20.0582 15.6865 20.0214 15.733 19.975L17.5 18.208L19.27 19.977C19.3161 20.0248 19.3713 20.0628 19.4323 20.0891C19.4933 20.1153 19.5589 20.129 19.6253 20.1296C19.6917 20.1302 19.7575 20.1176 19.819 20.0924C19.8804 20.0673 19.9363 20.0301 19.9832 19.9832C20.0301 19.9363 20.0673 19.8804 20.0924 19.819C20.1176 19.7575 20.1302 19.6917 20.1296 19.6253C20.129 19.5589 20.1153 19.4933 20.0891 19.4323C20.0628 19.3713 20.0248 19.3161 19.977 19.27L18.208 17.5L19.979 15.73C20.0701 15.6357 20.1205 15.5094 20.1193 15.3783C20.1182 15.2472 20.0656 15.1218 19.9729 15.0291C19.8802 14.9364 19.7548 14.8838 19.6237 14.8827C19.4926 14.8815 19.3663 14.9319 19.272 15.023L17.501 16.793L15.731 15.023C15.6372 14.9293 15.5101 14.8766 15.3775 14.8766C15.2449 14.8766 15.1178 14.9293 15.024 15.023M21 8.5L21.001 12.023C19.7529 11.2239 18.2692 10.8752 16.7958 11.0349C15.3224 11.1946 13.9478 11.8529 12.8999 12.9009C11.8519 13.9488 11.1936 15.3234 11.0339 16.7968C10.8742 18.2702 11.2229 19.7539 12.022 21.002L6.25 21C5.38805 21 4.5614 20.6576 3.9519 20.0481C3.34241 19.4386 3 18.612 3 17.75V8.5H21ZM17.75 3C18.612 3 19.4386 3.34241 20.0481 3.9519C20.6576 4.5614 21 5.38805 21 6.25V7H3V6.25C3 5.38805 3.34241 4.5614 3.9519 3.9519C4.5614 3.34241 5.38805 3 6.25 3H17.75Z"
            fill="#E53935"
          />
        </svg>
      ),
      type: "failure",
    },
    {
      label: "Email Required",
      //value: eventData?.summary?.EMAILREQUIRED?.totalGST,
      gst: eventData?.summary?.EMAILREQUIRED?.totalGST,
      value: eventData?.summary?.EMAILREQUIRED?.uniqueCount,
      type: "failure",
    },
    {
      label: "Invoice Received",
      gst: eventData?.summary?.["Invoice Received"]?.totalGST,
      value: eventData?.summary?.["Invoice Received"]?.count,

      // icon: (
      //   <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24">
      //     <path d="M6 3C5.5 3 5.1 3.2 4.8 3.6L3.5 5.2C3.2 5.6 3 6 3 6.5V19C3 20.1 3.9 21 5 21H13.3C13.1 20.4 13 19.7 13 19C13 15.7 15.7 13 19 13C19.7 13 20.4 13.1 21 13.3V6.5C21 6 20.8 5.6 20.5 5.2L19.1 3.5C18.9 3.2 18.5 3 18 3H6ZM5.9 4H17.9L18.8 5H5.1L5.9 4ZM6 15H12V18H6V15ZM21.3 15.8L17.7 19.4L16.1 17.8L15 19L17.8 22L22.6 17.2L21.3 15.8Z" fill="#0A8394" />
      //   </svg>
      // ),
      type: "success",
      //customStyle: { minWidth: 120, maxWidth: 140 }
    },
    {
      label: "Invoice Not Received",
      gst: eventData?.summary?.["Invoice Not Received"]?.totalGST,
      value: eventData?.summary?.["Invoice Not Received"]?.count,

      // icon: (
      //   <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
      //     <path d="M17.5 12C18.9587 12 20.3576 12.5795 21.3891 13.6109C22.4205 14.6424 23 16.0413 23 17.5C23 18.9587 22.4205 20.3576 21.3891 21.3891C20.3576 22.4205 18.9587 23 17.5 23C16.0413 23 14.6424 22.4205 13.6109 21.3891C12.5795 20.3576 12 18.9587 12 17.5C12 16.0413 12.5795 14.6424 13.6109 13.6109C14.6424 12.5795 16.0413 12 17.5 12ZM15.024 15.024C14.9303 15.1178 14.8776 15.2449 14.8776 15.3775C14.8776 15.5101 14.9303 15.6372 15.024 15.731L16.793 17.501L15.026 19.267C14.9795 19.3134 14.9426 19.3685 14.9174 19.4292C14.8923 19.4899 14.8793 19.5549 14.8792 19.6206C14.8792 19.6863 14.8921 19.7514 14.9172 19.8121C14.9423 19.8728 14.9791 19.928 15.0255 19.9745C15.0719 20.021 15.127 20.0579 15.1877 20.0831C15.2484 20.1082 15.3134 20.1212 15.3791 20.1213C15.4448 20.1213 15.5099 20.1084 15.5706 20.0833C15.6313 20.0582 15.6865 20.0214 15.733 19.975L17.5 18.208L19.27 19.977C19.3161 20.0248 19.3713 20.0628 19.4323 20.0891C19.4933 20.1153 19.5589 20.129 19.6253 20.1296C19.6917 20.1302 19.7575 20.1176 19.819 20.0924C19.8804 20.0673 19.9363 20.0301 19.9832 19.9832C20.0301 19.9363 20.0673 19.8804 20.0924 19.819C20.1176 19.7575 20.1302 19.6917 20.1296 19.6253C20.129 19.5589 20.1153 19.4933 20.0891 19.4323C20.0628 19.3713 20.0248 19.3161 19.977 19.27L18.208 17.5L19.979 15.73C20.0701 15.6357 20.1205 15.5094 20.1193 15.3783C20.1182 15.2472 20.0656 15.1218 19.9729 15.0291C19.8802 14.9364 19.7548 14.8838 19.6237 14.8827C19.4926 14.8815 19.3663 14.9319 19.272 15.023L17.501 16.793L15.731 15.023C15.6372 14.9293 15.5101 14.8766 15.3775 14.8766C15.2449 14.8766 15.1178 14.9293 15.024 15.023M21 8.5L21.001 12.023C19.7529 11.2239 18.2692 10.8752 16.7958 11.0349C15.3224 11.1946 13.9478 11.8529 12.8999 12.9009C11.8519 13.9488 11.1936 15.3234 11.0339 16.7968C10.8742 18.2702 11.2229 19.7539 12.022 21.002L6.25 21C5.38805 21 4.5614 20.6576 3.9519 20.0481C3.34241 19.4386 3 18.612 3 17.75V8.5H21ZM17.75 3C18.612 3 19.4386 3.34241 20.0481 3.9519C20.6576 4.5614 21 5.38805 21 6.25V7H3V6.25C3 5.38805 3.34241 4.5614 3.9519 3.9519C4.5614 3.34241 5.38805 3 6.25 3H17.75Z" fill="#E53935" />
      //   </svg>
      // ),
      type: "failure",
    },
  ];



  const sent = isNewUIDomain ? (bcdWidgetCounts?.emailSent ?? 0) : (eventData?.summary?.delivered ?? 0);
  const bounce = isNewUIDomain ? (bcdWidgetCounts?.bounce ?? 0) : (eventData?.summary?.bounce ?? 0);
  const dropped = isNewUIDomain ? (bcdWidgetCounts?.dropped ?? 0) : (eventData?.summary?.dropped ?? 0);
  const deferred = isNewUIDomain ? (bcdWidgetCounts?.deferred ?? 0) : (eventData?.summary?.deferred ?? 0);
  const emailRequired = isNewUIDomain ? (bcdWidgetCounts?.emailRequired ?? 0) : (eventData?.summary?.EMAILREQUIRED?.uniqueCount ?? 0);

  const gstinMismatch = isNewUIDomain ? (bcdWidgetCounts?.gstMismatch ?? 0) : (bounce + dropped + deferred);
  const otherNotSent = isNewUIDomain ? (bcdWidgetCounts?.otherNotSent ?? 0) : 0;
  const notSent = isNewUIDomain ? (bcdWidgetCounts?.emailNotSent ?? 0) : (gstinMismatch + emailRequired);
  const totalRecords = isNewUIDomain
    ? (eventData?.data?.grandTotalRow?.totalCount ?? (sent + notSent))
    : (sent + notSent);


  const pct = (n, total) => (total > 0 ? (n / total) * 100 : 0);
  const sentPct = Math.round(pct(sent, totalRecords));
  const notSentPct = Math.round(pct(notSent, totalRecords));
  const gstinPct = pct(gstinMismatch, notSent).toFixed(1);
  const emailReqPct = pct(emailRequired, notSent).toFixed(1);


  // chart options...

  const baseDonutSeries = {
    type: "donut",
    angleKey: "value",
    calloutLabelKey: "category",
    innerRadiusRatio: 0.68,
    strokes: ["#FFFFFF"],
    strokeWidth: 2,
    calloutLabel: { enabled: false },
    sectorLabel: { enabled: false },
    tooltip: {
      renderer: ({ datum }) => ({
        title: datum.category,
        content: Math.round(datum.value).toLocaleString(),
      }),
    },
  };

  const mailDispatchChartOptions = {
    data: [
      { category: "Sent", value: sent },
      { category: "Not sent", value: notSent },
    ],
    series: [{
      ...baseDonutSeries,
      fills: ["#26A69A", "#F87171"],
      innerRadiusRatio: 0.6,
      listeners: {
        nodeClick: (event) => {
          const category = event.datum?.category;
          if (category === "Sent") {
            setSelectedStatusLabel("Sent");
            setSelectedEmailStatus("email_sent");
          } else if (category === "Not sent") {
            setSelectedStatusLabel("Not Sent");
            setSelectedEmailStatus("email_not_sent");
          }
        },
      },
    }],
    background: { fill: "transparent" },
    legend: { enabled: false },
    padding: { top: 0, right: 0, bottom: 0, left: 0 },
  };

  const notSentChartOptions = {
    data: [
      { category: "GSTIN mismatch", value: gstinMismatch },
      { category: "Email required", value: emailRequired },
      { category: "Action Required", value: otherNotSent },
    ],
    series: [{
      ...baseDonutSeries,
      fills: ["#F87171", "#F59E0B", "#94A3B8"],
      innerRadiusRatio: 0.6,
      listeners: {
        nodeClick: (event) => {
          const category = event.datum?.category;
          if (category === "GSTIN mismatch") {
            setSelectedStatusLabel("GSTIN Mismatch");
            setSelectedEmailStatus("gstMismatch");
            setRefreshTrigger((prev) => prev + 1);
          } else if (category === "Email required") {
            setSelectedStatusLabel("Email Required");
            setSelectedEmailStatus("email_required");
            setRefreshTrigger((prev) => prev + 1);
          } else if (category === "Action Required") {
            setSelectedStatusLabel("Action Required");
            setSelectedEmailStatus("otherNotSent");
            setRefreshTrigger((prev) => prev + 1);
          }
        },
      },
    }],
    background: { fill: "transparent" },
    legend: { enabled: false },
    padding: { top: 0, right: 0, bottom: 0, left: 0 },
  };


  const handleEmailFilter = (status) => {
    const normalizedStatus = status === "All" ? "" : status.toLowerCase();
    setSelectedEmailStatus(normalizedStatus);
  };

  // removed unused gridRef

  const handleBookingIdFilter = (bookingId) => {
    if (agGridRef.current) {
      if (!bookingId) {
        agGridRef.current.setFilterModel({});
        return;
      }

      agGridRef.current.setFilterModel({
        email_bookingId: {
          type: "contains",
          filter: bookingId,
        },
      });
    }
  };


  const handleBookingIdFilterBulk = (bookingIdString) => {
    if (agGridRef.current) {
      if (!bookingIdString?.trim()) {
        agGridRef.current.setFilterModel({});
        return;
      }

      // Clean up the string to avoid extra spaces
      const cleanedBookingIdString = bookingIdString
        .split(",")
        .map((id) => id.trim())
        .filter((id) => id.length > 0)
        .join(",");

      agGridRef.current.setFilterModel({
        email_bookingId: {
          type: "custom", // Inform server-side filtering logic
          filter: cleanedBookingIdString, // e.g. "9041,9042,9043"
        },
      });

      agGridRef.current.onFilterChanged();
    }
  };



  const setModalType = () => { };
  const columnDefs = useMemo(() => {
    const baseColumnDefs =
      segmentType === "HOTEL"
        ? HOTEL_FOLLOWUP_COLUMN_DEFS({
          setModalType,
          setModalData,
          setIsModalVisible,
          openPhoneModal,
          openEmailModal,
          openResendModal,
          clientNamesMapRef,
          serviceType: hotelFollowUpType,
          isDevApp,
          domain,
          workspaceName,
        })
        : segmentType === "CLIENT"
          ? CLIENT_FOLLOWUP_COLUMN_DEFS({
            setModalType,
            setModalData,
            setIsModalVisible,
            openPhoneModal,
            openEmailModal,
            openResendModal,
          })
          : AIRLINE_FOLLOWUP_COLUMN_DEFS({
            setModalType,
            setModalData,
            setIsModalVisible,
            openPhoneModal,
            openEmailModal,
            openResendModal,
          });

    // Hide booking_client_id column when shouldHideUI is true (filter still active at server level)
    if (shouldHideUI) {
      return baseColumnDefs.map((col) => {
        if (col.field === "booking_client_id") {
          return {
            ...col,
            hide: true, // Hide the column completely
          };
        }
        return col;
      });
    }



    return baseColumnDefs;
  }, [
    segmentType,
    setModalType,
    setModalData,
    setIsModalVisible,
    shouldHideUI,
    openPhoneModal,
    openEmailModal,
    openResendModal,
    hotelFollowUpType,
  ]);

  const autoGroupColumnDef = useMemo(() => ({
    headerName: "Group",
    cellRenderer: "agGroupCellRenderer",
    enableRowGroup:false,
    checkboxSelection: (params) =>
      (params.node && params.node.field === "email_hotel_name") ||
      params.node.field === "airline_name",
    cellRendererParams: {
      innerRenderer: (params) => {
        const groupValue = params.node?.key || params.value;
        const count = params.data?.count;
        return (
          <div style={{ display: "flex", alignItems: "center" }}>
            <span>{groupValue}</span>
            {typeof count === "number" && (
              <span style={{ marginLeft: 8 }}>({count})</span>
            )}
          </div>
        );
      },
    },
  }), []);

  const emailOptions = [
    {
      label: "Monica (monica@kgrp.in)",
      value: "monica@kgrp.in",
      name: "Monica",
    },
    { label: "Aman (aman07@kgrp.in)", value: "aman07@kgrp.in", name: "Aman" },
    {
      label: "Pavithra (pavithra@kgrp.in)",
      value: "pavithra@kgrp.in",
      name: "Pavithra",
    },
    {
      label: "Rakesh (rakesh@kgrp.in)",
      value: "rakesh@kgrp.in",
      name: "Rakesh",
    },
    {
      label: "Chaitra (chaitra@kgrp.in)",
      value: "chaitra@kgrp.in",
      name: "Chaitra",
    },
    {
      label: "Shambhavi (shambhavi@kgrp.in)",
      value: "shambhavi@kgrp.in",
      name: "Shambhavi",
    },
    {
      label: "Ritwik (ritwik@kgrp.in)",
      value: "ritwik@kgrp.in",
      name: "Ritwik",
    },
    { label: "Default", value: "Default", name: "Default" },
  ];

  // Define sender options with name and email
  const senderOptions = [
    {
      label: "Monica (monica@kgrp.in)",
      value: "monica@kgrp.in",
      name: "Monica",
    },
    { label: "Aman (aman07@kgrp.in)", value: "aman07@kgrp.in", name: "Aman" },
    {
      label: "Pavithra (pavithra@kgrp.in)",
      value: "pavithra@kgrp.in",
      name: "Pavithra",
    },
    {
      label: "Rakesh (rakesh@kgrp.in)",
      value: "rakesh@kgrp.in",
      name: "Rakesh",
    },
    {
      label: "Chaitra (chaitra@kgrp.in)",
      value: "chaitra@kgrp.in",
      name: "Chaitra",
    },
    {
      label: "Shambhavi (shambhavi@kgrp.in)",
      value: "shambhavi@kgrp.in",
      name: "Shambhavi",
    },
    {
      label: "Ritwik (ritwik@kgrp.in)",
      value: "ritwik@kgrp.in",
      name: "Ritwik",
    },
    { label: "Default", value: "Default", name: "Default" },
  ];
  const templateOptions = [
    { label: "DEFAULT", value: "DEFAULT" },
    { label: "BOOKING_HOTEL", value: "BOOKING_HOTEL" },
    {
      label: "TO_TRAVELLER_PRIOR_CHECKIN",
      value: "TO_TRAVELLER_PRIOR_CHECKIN",
    },
    {
      label: "TO_TRAVELLER_ON_CHECKIN_DAY",
      value: "TO_TRAVELLER_ON_CHECKIN_DAY",
    },
    { label: "CHECKIN_HOTEL", value: "CHECKIN_HOTEL" },
    {
      label: "TO_TRAVELLER_BEFORE_CHECKOUT",
      value: "TO_TRAVELLER_BEFORE_CHECKOUT",
    },
    {
      label: "BEFORE_CHECKOUT_HOTEL",
      value: "BEFORE_CHECKOUT_HOTEL",
    },
    {
      label: "AFTER_CHECKOUT_HOTEL",
      value: "AFTER_CHECKOUT_HOTEL",
    },
    {
      label:"COMMON_TEMPLATE",
      value:"COMMON_TEMPLATE"
    }
  ];

  // removed editedPincode state
  const [editedRows, setEditedRows] = useState(new Map()); // Track multiple edited rows

  const handleCellEditingStarted = () => {
    const saveBtn = document.getElementById("saveBtn");
    if (saveBtn) {
      saveBtn.style.display = "flex";
      saveBtn.style.padding = "16px 40px";
      saveBtn.style.fontSize = "16px";
      saveBtn.style.borderRadius = "4px";
    }
  };

  // ✅ Monitor editedRows to show/hide save button
  useEffect(() => {
    const saveBtn = document.getElementById("saveBtn");
    if (saveBtn) {
      if (editedRows.size > 0) {
        saveBtn.style.display = "flex";
        saveBtn.style.padding = "16px 40px";
        saveBtn.style.fontSize = "16px";
        saveBtn.style.borderRadius = "4px";
      } else {
        saveBtn.style.display = "none";
      }
    }
  }, [editedRows]);

  // ✅ Clear edited rows when checkbox selection changes
  const handleSelectionChanged = useCallback(() => {
    const gridApi = agGridRef.current?.api;
    if (!gridApi) return;

    const selectedNodes = gridApi.getSelectedNodes();
    const dataNodes = selectedNodes.filter((node) => node.data);
    const selectedRowIds = dataNodes.map(
      (node) => node.data.email_bookingId || node.data._id,
    );

    setSelectedRowCount(dataNodes.length);

    // Remove any edited rows that are no longer selected
    setEditedRows((prev) => {
      const newMap = new Map();
      prev.forEach((value, key) => {
        if (selectedRowIds.includes(key)) {
          newMap.set(key, value);
        }
      });
      return newMap;
    });
  }, []);

  const handleSaveClick = async () => {
    // ✅ Finalize any edits
    agGridRef.current?.api?.stopEditing();

    try {
      // ✅ Get all edited rows from the Map
      const editedRowsArray = Array.from(editedRows.values());

      if (editedRowsArray.length === 0) {
        // no rows to save
        return;
      }

      // ✅ Process all edited rows - send as array of objects
      const records = editedRowsArray
        .map((selectedRow) => {
          // ✅ Check that row has required data
          if (!selectedRow.email_hotel_name) {
            // missing required field
            return null;
          }

          // ✅ Construct payload only with available fields
          const record = {
            email_hotel_name: selectedRow.email_hotel_name,
          };

          if ("pincode" in selectedRow) {
            record.pincode = selectedRow.pincode ?? "";
          }

          if (selectedRow.email_hotel_city) {
            record.city = selectedRow.email_hotel_city;
          }

          // ✅ Add email_bookingId if available
          if (selectedRow.email_bookingId) {
            record.bookingId = selectedRow.email_bookingId;
          }

          // ✅ Add remarks directly as fields (only if they were edited)
          if ("remark1" in selectedRow) {
            record.remark1 = selectedRow.remark1 ?? "";
          }
          if ("remark2" in selectedRow) {
            record.remark2 = selectedRow.remark2 ?? "";
          }

          return record;
        })
        .filter((record) => record !== null); // Remove null records

      if (records.length === 0) {
        // no valid records
        return;
      }

      // ✅ Send as array of objects for multiple remarks
      const payload = { records };
      const response = await fetch(
        "https://stage-apiplatform.finkraftai.com/api/hotelFollowUp/pincodeAddUpdate",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        },
      );

      if (!response.ok) throw new Error("Failed to save data");

      // ✅ Show success message
      message.success(`Successfully saved ${records.length} row(s)`);

      // ✅ Clear edited rows after successful save
      setEditedRows(new Map());

      const saveBtn = document.getElementById("saveBtn");
      if (saveBtn) saveBtn.style.display = "none";

      // ✅ Refresh grid data to reflect changes
      refreshGridData();
    } catch (error) {
      message.error("Failed to save changes. Please try again.");
    }
  };

  // convertFileToBase64 moved to module scope

  // Splits the combined "Hotel/Traveller Email Logs" columns into their hidden
  // per-log-type "First Mail" columns (defined in email.follow.helpers.js) for export.
  const EMAIL_LOGS_EXPORT_EXPANSION = {
    hotel_email_logs: [
      "hotel_first_mail_booking",
      "hotel_first_mail_checkin",
      "hotel_first_mail_before_checkout",
      "hotel_first_mail_after_checkout",
    ],
    traveller_email_logs: [
      "traveller_first_mail_prior_checkin",
      "traveller_first_mail_checkin_day",
      "traveller_first_mail_before_checkout",
    ],
  };

  const [exportProgress, setExportProgress] = useState(null); // { loaded, total } | null while exporting
  const exportCancelledRef = useRef(false);

  // Formats a single cell value the same way the old ag-grid processCellCallback did.
  const formatExportCellValue = (colId, value) => {
    if (value && typeof value === "object") {
      if (colId === "traveller_info") {
        const traveller = value;
        if (traveller.name || traveller.email || traveller.phone) {
          return `${traveller.name || ""} | ${traveller.email || ""} | ${traveller.phone || ""}`.trim();
        }
        return JSON.stringify(traveller);
      }

      if (colId === "email_logs") {
        if (Array.isArray(value)) {
          return value
            .map((log) =>
              typeof log === "object"
                ? `${log.status || ""} - ${log.timestamp || ""} - ${log.message || ""}`.trim()
                : log,
            )
            .join("; ");
        }
        return JSON.stringify(value);
      }

      return JSON.stringify(value);
    }

    return value;
  };

  // Mirrors AgTableServer's row normalization so exported rows match what the grid renders.
  const processExportValue = (value) => {
    if (typeof value === "string" && !isNaN(value) && value.trim() !== "") {
      if (value.trim().startsWith("0") && value.trim().length > 1) return value;
      return Number(value);
    }
    if (value === null || value === undefined) return value;
    if (value instanceof Date) return value;
    if (Array.isArray(value)) return value.map(processExportValue);
    if (typeof value === "object") return value;
    return value;
  };

  const flattenExportRow = (inputRow) => {
    const out = {};
    const walk = (val, prefix) => {
      if (val === null || val === undefined) return;
      if (typeof val === "object" && !Array.isArray(val) && !(val instanceof Date)) {
        Object.entries(val).forEach(([k, v]) => walk(v, prefix ? `${prefix}_${k}` : k));
        return;
      }
      out[prefix] = processExportValue(val);
    };
    Object.entries(inputRow || {}).forEach(([k, v]) => walk(v, k));
    return out;
  };

  const buildHeaderMap = (defs) => {
    const map = {};
    (defs || []).forEach((col) => {
      if (col.children) {
        Object.assign(map, buildHeaderMap(col.children));
      } else if (col.field) {
        map[col.field] = col.headerName || col.field;
      }
    });
    return map;
  };

  // Exports ALL rows matching the current filters, not just the blocks the
  // server-side row model has loaded into the grid so far (see AgTableServer,
  // rowModelType="serverSide" / cacheBlockSize=100 — the grid only holds
  // whatever's been scrolled into view, so the old exportDataAsExcel() only
  // ever exported the loaded subset). We re-fetch the full dataset directly
  // from the same endpoint the grid already uses, in blocks, then build the
  // sheet client-side.
  const exportToExcel = async () => {
    if (!agGridRef.current) {
      message.error("Grid not ready for export");
      return;
    }

    const gridApi = agGridRef.current.api;
    const columnApi = agGridRef.current.columnApi;

    const displayedColIds = (columnApi?.getAllDisplayedColumns() || []).map((col) => col.getColId());
    const columnKeys = displayedColIds.flatMap(
      (colId) => EMAIL_LOGS_EXPORT_EXPANSION[colId] || [colId],
    );

    if (!columnKeys.length) {
      message.error("No columns to export");
      return;
    }

    const filterModel = gridApi?.getFilterModel() || {};
    const sortModel = (columnApi?.getColumnState() || [])
      .filter((col) => col.sort)
      .sort((a, b) => (a.sortIndex ?? 0) - (b.sortIndex ?? 0))
      .map((col) => ({ colId: col.colId, sort: col.sort }));

    const CHUNK_SIZE = 500;
    // The backend doesn't reliably signal "no more rows" with a short last
    // chunk, so cap on the record count the UI already shows (falling back to
    // a hard ceiling only if that count isn't available) instead of looping
    // until a short response finally shows up.
    const expectedTotal = totalRecords > 0 ? totalRecords : 100000;

    exportCancelledRef.current = false;
    setExportProgress({ loaded: 0, total: expectedTotal });

    try {
      let allRows = [];
      let startRow = 0;

      while (allRows.length < expectedTotal) {
        if (exportCancelledRef.current) {
          message.info("Export cancelled");
          return;
        }

        const endRow = startRow + CHUNK_SIZE;
        const response = await apiPost(hotelFollowUpEndpoint, {
          startRow,
          endRow,
          rowGroupCols: [],
          valueCols: [],
          pivotCols: [],
          pivotMode: false,
          groupKeys: [],
          filterModel,
          sortModel,
          ...serverPayload,
          userDetails,
          activeTab,
          tableName: activeTableName,
        });

        if (exportCancelledRef.current) {
          message.info("Export cancelled");
          return;
        }

        const rowsCandidate = response?.data?.rows ?? response?.rows ?? [];
        const chunk = Array.isArray(rowsCandidate) ? rowsCandidate : [];
        allRows = allRows.concat(chunk);
        setExportProgress({ loaded: allRows.length, total: expectedTotal });

        const lastRow = response?.data?.lastRow ?? response?.lastRow;
        if (chunk.length === 0) break; // no more data, regardless of expectedTotal
        if (typeof lastRow === "number" && lastRow >= 0 && allRows.length >= lastRow) break;
        if (chunk.length < CHUNK_SIZE) break;

        startRow = endRow;
      }

      if (!allRows.length) {
        message.error("No data returned for export");
        return;
      }

      const rows = allRows.map((row) => {
        const processed = Object.fromEntries(
          Object.entries(row).map(([k, v]) => [k, processExportValue(v)]),
        );
        return { ...processed, ...flattenExportRow(processed) };
      });

      const headerMap = buildHeaderMap(columnDefs);
      const sheetRows = [
        columnKeys.map((colId) => headerMap[colId] || colId),
        ...rows.map((row) => columnKeys.map((colId) => formatExportCellValue(colId, row[colId]))),
      ];

      const worksheet = XLSX.utils.aoa_to_sheet(sheetRows);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Export");
      XLSX.writeFile(workbook, `Hotel_Email_Export_${moment().format("YYYY-MM-DD_HH-mm-ss")}.xlsx`);
    } catch (error) {
      if (!exportCancelledRef.current) {
        message.error("Export failed. Please try again.");
      }
    } finally {
      setExportProgress(null);
    }
  };
  const handleResetFilter = () => {
    setSelectedEmailStatus("");
    setSelectedStatusLabel("");
    setSelectedHotelName("");
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", flex: 1 }}>
      <PageHeader
        title="Email Followup"
        rightActions={
          (isDevApp || (isInternalUser && !isBcd && domain?.toLowerCase() !== "app")) ? (
            <>
              {pendingUpdates.length > 0 && (
                <Button
                  size="small"
                  type="primary"
                  onClick={handleUpdateClick}
                  style={{ marginRight: 8 }}
                >
                  Update
                </Button>
              )}

              <Button
                size="small"
                type="primary"
                onClick={() => navigate("/email_followup_history")}
                style={{ marginRight: 8 }}
              >
                History
              </Button>

              <Button
                size="small"
                type="primary"
                onClick={() => navigate("/email_followup_config_list")}
                style={{ marginRight: 8 }}
              >
                View Config
              </Button>

              <Button
                size="small"
                type="primary"
                onClick={() => setAddConfigModalOpen(true)}
                style={{ marginRight: 8 }}
              >
                Add Config
              </Button>

              {isDevApp && (
                <Button
                  size="small"
                  type="primary"
                  onClick={() => navigate("/vendor_master")}
                  style={{ marginRight: 8 }}
                >
                  Vendor Master
                </Button>
              )}
            </>
          ) : null
        }
      />

      {/* Add Config Modal */}
      <Modal
        open={addConfigModalOpen}
        onCancel={() => {
          setAddConfigModalOpen(false);
          setConfigClientType(null);
          setConfigSelectedWorkspace(null);
          setConfigTmcList([]);
        }}
        onOk={() => {
          if (!configClientType) { message.warning("Please select a client type."); return; }
          if (!configSelectedWorkspace) { message.warning("Please select a workspace."); return; }
          const selected = configClientType === "DIRECT"
            ? configDirectList.find((c) => c.name === configSelectedWorkspace)
            : configTmcList.find((t) => t.domain === configSelectedWorkspace);
          const savedClientType = configClientType;
          setAddConfigModalOpen(false);
          setConfigClientType(null);
          setConfigSelectedWorkspace(null);
          setConfigTmcList([]);
          setConfigDirectList([]);
          navigate("/email_followup_config", {
            state: {
              action: "create",
              clientType: savedClientType,
              workspaceName: selected?.name ?? "",
              workspaceId: selected?.id ?? "",
              tmcDomain: selected?.domain ?? null,
            },
          });
        }}
        okText="Continue"
        cancelText="Cancel"
        okButtonProps={{ style: { background: "#0a8394", borderColor: "#0a8394" } }}
        title="New Configuration"
        width={680}
        centered
        zIndex={10000}
      >
        {/* Client Type cards */}
        <div style={{ marginBottom: 24, marginTop: 16 }}>
          <div style={{ marginBottom: 10, fontSize: 12, fontWeight: 500 }}>
            Client Type <span style={{ color: "#ff4d4f" }}>*</span>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {[
              { key: "DIRECT", label: "Direct Client", description: "Client who directly works with your organization.", icon: <UserOutlined /> },
              { key: "TMC", label: "TMC / Channel Partner", description: "Travel management company or channel partner.", icon: <TeamOutlined /> },
            ].map((t) => (
              <div
                key={t.key}
                onClick={async () => {
                  setConfigClientType(t.key);
                  setConfigSelectedWorkspace(null);
                  setConfigListLoading(true);
                  if (t.key === "TMC") {
                    const res = await apiGet(API_ENDPOINT_FETCH_TMCLISTS);
                    // const res = await apiGet("http://192.168.0.13:7002/api/misc/tmc/list");
                    if (res.status) setConfigTmcList(res.data);
                  } else {
                    const res = await apiGet(API_ENDPOINT_FETCH_TMCLISTS_DIRECT);
                    // const res = await apiGet("http://192.168.0.13:7002/api/misc/tmc/directList");
                    if (res.status) setConfigDirectList(res.data);
                  }
                  setConfigListLoading(false);
                }}
                style={{
                  display: "flex", alignItems: "center", gap: 12,
                  border: `1.5px solid ${configClientType === t.key ? "#0a8394" : "#e8e8e8"}`,
                  borderRadius: 10, padding: "12px 16px", cursor: "pointer",
                  background: configClientType === t.key ? "rgba(10,131,148,0.03)" : "#fff",
                  boxShadow: configClientType === t.key ? "0 2px 12px rgba(10,131,148,0.12)" : "0 1px 4px rgba(0,0,0,0.04)",
                  transition: "all 0.2s",
                }}
              >
                <div style={{ width: 36, height: 36, borderRadius: 8, flexShrink: 0, background: "rgba(10,131,148,0.08)", display: "flex", alignItems: "center", justifyContent: "center", color: "#0a8394", fontSize: 16 }}>
                  {t.icon}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>{t.label}</div>
                  <div style={{ fontSize: 12, color: "#595959" }}>{t.description}</div>
                </div>
                <div style={{ width: 18, height: 18, borderRadius: "50%", flexShrink: 0, border: `2px solid ${configClientType === t.key ? "#0a8394" : "#d0d0d0"}`, background: "#fff", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  {configClientType === t.key && <div style={{ width: 9, height: 9, borderRadius: "50%", background: "#0a8394" }} />}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Workspace / TMC dropdown — shown after client type is selected */}
        {configClientType && (
          <div>
            <div style={{ marginBottom: 6, fontSize: 12, fontWeight: 500 }}>
              {configClientType === "DIRECT" ? "Workspace" : "TMC"}{" "}
              <span style={{ color: "#ff4d4f" }}>*</span>
            </div>
            <Select
              showSearch
              allowClear
              loading={configListLoading}
              value={configSelectedWorkspace}
              placeholder={configClientType === "DIRECT" ? "Select direct client" : "Select TMC"}
              style={{ width: "100%", height: 38 }}
              optionFilterProp="label"
              options={
                configClientType === "DIRECT"
                  ? configDirectList.filter((c) => c.name).map((c) => ({ value: c.name, label: c.name }))
                  : configTmcList.map((t) => ({ value: t.domain, label: t.name }))
              }
              onChange={(val) => setConfigSelectedWorkspace(val)}
            />
          </div>
        )}
      </Modal>

      {!shouldHideUI && (
        <div style={{ display: "flex", alignItems: "flex-start" }}>
          {domain !== "myyatra" && domain !== "bcd" ? (
            <div style={{ padding: "8px 12px", display: "flex", flexDirection: "column" }}>
              <Typography variant="xs" style={{ marginBottom: 4 }}>
                Select Segment
              </Typography>
              <Select
                value={segmentType}
                onChange={(type) => {
                  setSegmentType(type);
                  setSelectedClient("");
                  if (!isBcd) setHotelFollowUpType(null);
                }}
                style={{ width: 180, height: 34 }}
                placeholder="Select Segment"
                allowClear
              >
                {[
                  { key: "HOTEL", name: "Hotel" },
                  { key: "AIRLINE", name: "Airline" },
                  { key: "CLIENT", name: "Client" },
                ].map((item, key) => (
                  <Option key={key} value={item.key}>
                    {item.name}
                  </Option>
                ))}
              </Select>
            </div>
          ) : null}

          {domain?.toLowerCase() === "app" && segmentType === "HOTEL" && (
            <div style={{ padding: "8px 12px", display: "flex", flexDirection: "column" }}>
              <Typography variant="xs" style={{ marginBottom: 4 }}>
                Follow Up Type
              </Typography>
              <Select
                value={hotelFollowUpType}
                onChange={(val) => setHotelFollowUpType(val)}
                style={{ width: 200, height: 34 }}
                placeholder="Select Type"
                allowClear
              >
                <Option value="INVOICE_FOLLOWUP">Invoice Follow Up</Option>
                <Option value="GSTIN_TRANSMISSION">GSTIN Transmission</Option>
              </Select>
            </div>
          )}

          {domain !== "myyatra" &&
            segmentType &&
            segmentType !== "Choose Segment" ? (
            <div style={{ padding: "8px 12px" }}>
              <Typography variant="xs" style={{ marginBottom: 4 }}>
                {segmentType === "HOTEL"
                  ? "Select Hotel Client"
                  : segmentType === "AIRLINE"
                    ? "Select Airline Client"
                    : segmentType === "CLIENT"
                      ? "Select Client"
                      : "Select Client"}
              </Typography>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Select
                  mode="multiple"
                  className="compact-client-select"
                  value={selectedClient || undefined}
                  onChange={handleChange}
                  style={{ width: 220, height: 33 }}
                  placeholder={
                    segmentType === "HOTEL"
                      ? "Select Hotel Client"
                      : segmentType === "AIRLINE"
                        ? "Select Airline Client"
                        : "Select Client"
                  }
                  allowClear
                  showSearch
                  tagRender={(props) => {
                    const { value, closable, onClose } = props;
                    const selectedArr = Array.isArray(selectedClient) ? selectedClient : [];
                    const idx = selectedArr.indexOf(value);
                    const clientName = clientNames.find((c) => c.client_id === value)?.client_name || value;

                    if (idx === 0) {
                      return (
                        <span style={{
                          display: "inline-flex", alignItems: "center", gap: 4,
                          background: "rgba(10,131,148,0.08)",
                          border: "1px solid rgba(10,131,148,0.35)",
                          borderRadius: 4,
                          padding: "0 6px 0 8px",
                          fontSize: 12,
                          color: "#0A8394",
                          height: 22,
                          maxWidth: 140,
                          overflow: "hidden",
                          whiteSpace: "nowrap",
                          flexShrink: 0,
                        }}>
                          <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{clientName}</span>
                          {closable && (
                            <span
                              onClick={(e) => { e.stopPropagation(); onClose(); }}
                              style={{ cursor: "pointer", color: "#0A8394", fontSize: 14, lineHeight: 1, flexShrink: 0, opacity: 0.7 }}
                            >×</span>
                          )}
                        </span>
                      );
                    }

                    if (idx === 1) {
                      const remaining = selectedArr.slice(1);
                      const remainingItems = remaining.map((id) => ({
                        id,
                        name: clientNames.find((c) => c.client_id === id)?.client_name || id,
                      }));
                      return (
                        <Tooltip
                          color="#1C1C1C"
                          overlayInnerStyle={{ padding: 10, minWidth: 180, borderRadius: 8 }}
                          title={
                            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                              {remainingItems.map((item) => (
                                <div key={item.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
                                  <span style={{ fontSize: 12, color: "#fff" }}>{item.name}</span>
                                  <span
                                    style={{ cursor: "pointer", color: "#aaa", fontSize: 16, lineHeight: 1, padding: "0 2px", flexShrink: 0 }}
                                    onMouseDown={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      handleChange(selectedArr.filter((id) => id !== item.id));
                                    }}
                                  >×</span>
                                </div>
                              ))}
                            </div>
                          }
                        >
                          <span style={{
                            background: "#e6f4ff", color: "#1677ff", borderRadius: 999,
                            padding: "0 8px", fontSize: 12, fontWeight: 500,
                            cursor: "pointer", whiteSpace: "nowrap",
                            display: "inline-flex", alignItems: "center",
                            height: 22, marginLeft: 4, flexShrink: 0,
                          }}>
                            +{remaining.length} more
                          </span>
                        </Tooltip>
                      );
                    }

                    // idx > 1: already counted in "+N more", render nothing
                    return <span key={value} style={{ display: "none" }} />;
                  }}
                  filterOption={(input, option) =>
                    (option?.children ?? "")
                      .toString()
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                >
                  {(() => {
                    const filtered = (clientNames || []).filter(
                      (item) => !segmentType || item.type?.toUpperCase() === segmentType
                    );
                    const allSelected =
                      Array.isArray(selectedClient) &&
                      filtered.length > 0 &&
                      filtered.every((item) => selectedClient.includes(item.client_id));
                    return (
                      <>
                        <Option key="__all__" value="__all__">
                          <span style={{ fontWeight: 600, color: allSelected ? "#0A8394" : undefined }}>
                            {allSelected ? "✓ All Selected" : "Select All"}
                          </span>
                        </Option>
                        {filtered.map((item, key) => (
                          <Option key={key} value={item?.client_id}>
                            {item?.client_name}
                          </Option>
                        ))}
                      </>
                    );
                  })()}
                </Select>
              </div>
            </div>
          ) : null}

          {isNewUIDomain && hotelFollowUpType !== "INVOICE_FOLLOWUP" && (
            <div style={{ padding: "8px 12px" }}>
              <Typography variant="xs" style={{ marginBottom: 4 }}>
                Date Filter
              </Typography>

              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Popover
                  open={datePickerOpen}
                  onOpenChange={setDatePickerOpen}
                  trigger="click"
                  placement="bottomLeft"
                  overlayInnerStyle={{ padding: 0, borderRadius: 6, overflow: "hidden" }}
                  content={
                    <div style={{ display: "flex", width: 480, fontSize: 13 }}>
                      {/* Left: absolute range */}
                      <div
                        style={{
                          flex: "0 0 200px",
                          padding: 16,
                          borderRight: "1px solid #f0f0f0",
                        }}
                      >
                        <div
                          style={{
                            fontWeight: 600,
                            marginBottom: 12,
                            color: "#333",
                          }}
                        >
                          Absolute time range
                        </div>

                        <div style={{ marginBottom: 10 }}>
                          <div
                            style={{
                              fontSize: 12,
                              color: "#888",
                              marginBottom: 4,
                            }}
                          >
                            From
                          </div>

                          <DatePicker
                            size="small"
                            style={{ width: "100%" }}
                            value={fromDate}
                            format="DD/MM/YYYY"
                            onChange={(d) => setFromDate(d)}
                            allowClear
                          />
                        </div>

                        <div style={{ marginBottom: 14 }}>
                          <div
                            style={{
                              fontSize: 12,
                              color: "#888",
                              marginBottom: 4,
                            }}
                          >
                            To <span style={{ color: "#bbb" }}>(optional)</span>
                          </div>

                          <DatePicker
                            size="small"
                            style={{ width: "100%" }}
                            value={toDate}
                            format="DD/MM/YYYY"
                            onChange={(d) => setToDate(d)}
                            allowClear
                            disabledDate={(curr) =>
                              fromDate && curr && curr.isBefore(fromDate, "day")
                            }
                          />
                        </div>

                        <button
                          className="datePresetBtn active"
                          style={{
                            width: "100%",
                            textAlign: "center",
                            padding: "5px 0",
                          }}
                          disabled={!fromDate}
                          onClick={() => {
                            if (!fromDate) return;

                            const value = toDate
                              ? `${fromDate.format("YYYY-MM-DD")},${toDate.format(
                                "YYYY-MM-DD"
                              )}`
                              : fromDate.format("YYYY-MM-DD");

                            setDateFilter(value);
                            setActiveDatePreset("custom");
                            setDatePickerOpen(false);
                          }}
                        >
                          Apply time range
                        </button>
                      </div>

                      {/* Right: quick presets */}
                      <div style={{ flex: 1 }}>
                        <div
                          style={{
                            padding: "12px 14px 6px",
                            fontSize: 12,
                            color: "#888",
                            borderBottom: "1px solid #f5f5f5",
                          }}
                        >
                          Quick ranges
                        </div>

                        {[
                          { label: "Today", key: "today" },
                          { label: "Yesterday", key: "yesterday" },
                          { label: "Last 7 days", key: "last7" },
                          { label: "Last 30 days", key: "last30" },
                        ].map(({ label, key }) => (
                          <div
                            key={key}
                            className={`dateQuickPreset${activeDatePreset === key ? " active" : ""
                              }`}
                            onClick={() => {
                              const t = moment();
                              let value = "";

                              if (key === "today") {
                                value = t.format("YYYY-MM-DD");
                              } else if (key === "yesterday") {
                                value = t
                                  .clone()
                                  .subtract(1, "days")
                                  .format("YYYY-MM-DD");
                              } else if (key === "last7") {
                                value = `${t
                                  .clone()
                                  .subtract(6, "days")
                                  .format("YYYY-MM-DD")},${t.format("YYYY-MM-DD")}`;
                              } else if (key === "last30") {
                                value = `${t
                                  .clone()
                                  .subtract(29, "days")
                                  .format("YYYY-MM-DD")},${t.format("YYYY-MM-DD")}`;
                              }

                              setActiveDatePreset(key);
                              setDateFilter(value);
                              setFromDate(null);
                              setToDate(null);
                              setDatePickerOpen(false);
                            }}
                          >
                            {label}
                          </div>
                        ))}
                      </div>
                    </div>
                  }
                >
                  <button
                    className={`dateFilterTrigger${dateFilter ? " hasValue" : ""
                      }`}
                  >
                    <svg
                      width="13"
                      height="13"
                      viewBox="0 0 24 24"
                      fill="none"
                      style={{ flexShrink: 0 }}
                    >
                      <rect
                        x="3"
                        y="4"
                        width="18"
                        height="18"
                        rx="2"
                        stroke="currentColor"
                        strokeWidth="2"
                      />

                      <path
                        d="M16 2v4M8 2v4M3 10h18"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                    </svg>

                    <span style={{ flex: 1 }}>{dateFilterLabel}</span>

                    {dateFilter && (
                      <span
                        className="dateFilterClear"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDateFilter("");
                          setActiveDatePreset("");
                          setFromDate(null);
                          setToDate(null);
                        }}
                      >
                        ×
                      </span>
                    )}
                  </button>
                </Popover>
                {(selectedEmailStatus || selectedHotelName) && (
                  <button
                    onClick={handleResetFilter}
                    style={{
                      cursor: "pointer",
                      fontSize: 12,
                      fontWeight: 500,
                      whiteSpace: "nowrap",
                      padding: "6px 10px",
                      borderRadius: 4,
                      border: "1px solid #d9d9d9",
                      background: "#fff",
                      color: "#555",
                      display: "flex",
                      alignItems: "center",
                      gap: 5,
                    }}
                  >
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none">
                      <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
                    </svg>
                    Clear Filters
                  </button>
                )}
              </div>
            </div>
          )}

          <div
            style={{
              marginLeft: "auto",
              padding: "8px 12px",
              display: "flex",
              gap: "15px",
            }}
          >
            {(isBcd ||
              (domain === "app" &&
                [].concat(selectedClient).some((id) => id?.startsWith("BCD_")))) && (
                <Button
                  type="primary"
                  style={{ margin: "12px 0" }}
                  onClick={async () => {
                    setIsCustomerInfoModalVisible(true);
                    await fetchCustomerInfo();
                  }}
                >
                  View Customer Info
                </Button>
              )}
            <Button
              type="primary"
              style={{ margin: "12px 0" }}
              onClick={exportToExcel}
              loading={!!exportProgress}
            >
              Export Table
            </Button>
            <Modal
              open={!!exportProgress}
              title="Exporting…"
              closable={false}
              maskClosable={false}
              footer={[
                <Button
                  key="cancel"
                  danger
                  onClick={() => {
                    exportCancelledRef.current = true;
                  }}
                >
                  Cancel
                </Button>,
              ]}
            >
              <p>
                Fetched {exportProgress?.loaded ?? 0} of {exportProgress?.total ?? 0} records…
              </p>
            </Modal>
            {((!isBcd && hotelFollowUpType !== "GSTIN_TRANSMISSION") || hotelFollowUpType === "INVOICE_FOLLOWUP") && (
              <Button
                type="primary"
                style={{ margin: "12px 0" }}
                onClick={() => setIsBulkMailModalVisible(true)}
              >
                Bulk Mail
              </Button>
            )}
            {isNewUIDomain && hotelFollowUpType !== "INVOICE_FOLLOWUP" && (
              <Button
                type="primary"
                style={{ margin: "12px 0" }}
                disabled={selectedRowCount === 0}
                onClick={() => setIsBulkSingleMailModalVisible(true)}
              >
                {selectedRowCount > 0
                  ? `Send Mail (${selectedRowCount} selected)`
                  : "Send Mail"}
              </Button>
            )}

            <Button
              id="saveBtn"
              type="primary"
              className="savekBtnFollowup"
              style={{ display: "none" }}
              onClick={handleSaveClick}
            >
              Save {editedRows.size > 0 ? `(${editedRows.size} rows)` : ""}
            </Button>
          </div>
        </div>
      )}

      {/* Add the button to get selected rows */}

      {/* new ui---ishika */}

      {/* {!shouldHideUI && (
        <div className="logsContainer" style={{ display: "flex", gap: 16 }}>
          {loading || !eventData
            ? Array.from({ length: 6 }).map((_, index) => (
                <div
                  key={index}
                  style={{
                    flex: 1,
                    // flex: customStyle && customStyle.flex ? customStyle.flex : 1,
                    padding: 20,
                    borderRadius: 12,
                    background: "#f9f9f9",
                    minWidth: 150,

                    // boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
                  }}
                >
                  <Skeleton
                    active
                    title={false}
                    paragraph={{ rows: 2, width: ["40%", "60%"] }} // label & value placeholder
                  />
                </div>
              ))
            : summaryCards.map((item, index) => (
                <StatusCard
                  key={index}
                  label={item.label}
                  value={item.value}
                  icon={item.icon}
                  type={item.type}
                  isSelected={selectedStatusLabel === item.label}hotelFollowUpType
                  onClick={() => {
                    setSelectedStatusLabel(item.label);
                    handleEmailFilter(item.label);
                  }}
                  customStyle={item.customStyle}
                  gst={item.gst}
                />
              ))}
        </div>
      )} */}


      {!shouldHideUI && (
        (domain?.toLowerCase() === "bcd" || domain?.toLowerCase() === "app") ? (
          /* ===== NEW UI — BCD + app ===== */
          <div className="mailSummaryContainer">
            {loading || !eventData ? (
              <>
                {Array.from({ length: 3 }).map((_, i) => (
                  <div key={i} className="summaryCardLight">
                    <Skeleton active paragraph={{ rows: 4 }} />
                  </div>
                ))}
              </>
            ) : (
              <>
                {/* Card 1: Mail Dispatch */}
                <div className="summaryCardLight">
                  <div className="cardTitleRow">
                    <div className="cardTitle">MAIL DISPATCH</div>
                    <div className="badge" style={{ background: "#dcfce7", color: "#166534" }}>
                      <span style={{ fontWeight: 700 }}>
                        {(bcdWidgetCounts?.overallUniqueHotelCount ?? 0).toLocaleString()}
                      </span>
                      &nbsp;Unique Hotels
                    </div>
                  </div>
                  <div className="cardContent">
                    <div className="chartWrapper">
                      <AgCharts options={mailDispatchChartOptions} style={{ height: '138px' }} />
                    </div>
                    <div className="statsWrapper">
                      <div className="muted" style={{ cursor: "pointer" }} onClick={() => { setSelectedStatusLabel(""); setSelectedEmailStatus(""); }}>Total records</div>
                      <div className="bigNumber" style={{ cursor: "pointer" }} onClick={() => { setSelectedStatusLabel(""); setSelectedEmailStatus(""); }}>{totalRecords.toLocaleString()}</div>
                      <div className="splitRow">
                        <div
                          className="splitItem"
                          onClick={() => {
                            setSelectedStatusLabel("Sent");
                            setSelectedEmailStatus("email_sent");
                          }}
                        >
                          <div className="legendRow">
                            <span className="dot" style={{ background: "#26A69A" }} />
                            <span className="muted">Sent</span>
                          </div>
                          <div className="value green">{sent.toLocaleString()}</div>
                        </div>
                        <div
                          className="splitItem"
                          onClick={() => {
                            setSelectedStatusLabel("Not Sent");
                            setSelectedEmailStatus("email_not_sent");
                          }}
                        >
                          <div className="legendRow">
                            <span className="dot" style={{ background: "#F87171" }} />
                            <span className="muted">Not sent</span>
                          </div>
                          <div className="value red">{notSent.toLocaleString()}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Card 2: Not Sent — Why? */}
                <div className="summaryCardLight">
                  <div className="cardTitleRow">
                    <div className="cardTitle">NOT SENT</div>
                    <div className="badge">
                      <span style={{ fontWeight: 700 }}>
                        {(bcdWidgetCounts?.uniqueNotSentHotelCount ?? 0).toLocaleString()}
                      </span>
                      &nbsp;Unique Hotels
                    </div>
                  </div>
                  <div className="cardContent">
                    <div className="chartWrapper">
                      <AgCharts options={notSentChartOptions} style={{ height: '138px' }} />
                    </div>
                    <div className="statsWrapper">
                      <div
                        className="legendItem"
                        style={{ cursor: "pointer" }}
                        onClick={() => {
                          setSelectedStatusLabel("GSTIN Mismatch");
                          setSelectedEmailStatus("gstMismatch");
                          setRefreshTrigger((prev) => prev + 1);
                        }}
                      >
                        <div className="legendRow">
                          <span className="dot" style={{ background: "#F87171" }} />
                          <span className="muted">GSTIN Not Registered</span>
                        </div>
                        <div className="value red">
                          {gstinMismatch.toLocaleString()}
                        </div>
                      </div>
                      <div
                        className="legendItem"
                        style={{ marginTop: 16, cursor: "pointer" }}
                        onClick={() => {
                          setSelectedStatusLabel("Email Required");
                          setSelectedEmailStatus("email_required");
                          setRefreshTrigger((prev) => prev + 1);
                        }}
                      >
                        <div className="legendRow">
                          <span className="dot" style={{ background: "#F59E0B" }} />
                          <span className="muted">Email required</span>
                        </div>
                        <div className="value amber">
                          {emailRequired.toLocaleString()}
                        </div>
                      </div>
                      <div
                        className="legendItem"
                        style={{ marginTop: 16, cursor: "pointer" }}
                        onClick={() => {
                          setSelectedStatusLabel("Action Required");
                          setSelectedEmailStatus("otherNotSent");
                          setRefreshTrigger((prev) => prev + 1);
                        }}
                      >
                        <div className="legendRow">
                          <span className="dot" style={{ background: "#94A3B8" }} />
                          <span className="muted">Action Required</span>
                        </div>
                        <div className="value" style={{ color: "#94A3B8" }}>
                          {otherNotSent.toLocaleString()}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Card 3: All Status (devApp) / Top Hotels (BCD + others) */}
                <div className="summaryCardLight">
                  {isDevApp ? (
                    <>
                      <div className="cardTitle" style={{ marginLeft: 24, marginBottom: 12 }}>ALL STATUS</div>
                      <div style={{ padding: "0 24px" }}>
                        {[
                          {
                            label: "Bounce", value: bounce, status: "bounce",
                            icon: <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M14.143 11.486C14.2842 11.2691 14.504 11.1154 14.7562 11.0572C15.0085 10.999 15.2734 11.0409 15.4954 11.174C15.7174 11.3071 15.8791 11.5211 15.9465 11.771C16.014 12.021 15.9819 12.2873 15.857 12.514C14.355 15.019 13.447 17.404 12.987 20.164C12.827 21.12 11.539 21.314 11.106 20.447C9.04598 16.327 7.24798 15.471 4.31599 16.449C4.19136 16.4905 4.05978 16.507 3.92876 16.4977C3.79775 16.4883 3.66985 16.4533 3.55239 16.3945C3.31515 16.2758 3.13479 16.0677 3.05099 15.816C2.96718 15.5643 2.98678 15.2896 3.1055 15.0524C3.22421 14.8152 3.43229 14.6348 3.68399 14.551C6.88399 13.484 9.33998 14.178 11.487 17.174L11.578 17.304L11.589 17.264C12.111 15.436 12.856 13.714 13.862 11.964L14.143 11.486ZM18 4C17.606 4 17.2159 4.0776 16.8519 4.22836C16.488 4.37913 16.1572 4.6001 15.8787 4.87868C15.6001 5.15726 15.3791 5.48797 15.2283 5.85195C15.0776 6.21593 15 6.60603 15 7C15 7.39397 15.0776 7.78407 15.2283 8.14805C15.3791 8.51203 15.6001 8.84274 15.8787 9.12132C16.1572 9.3999 16.488 9.62087 16.8519 9.77164C17.2159 9.9224 17.606 10 18 10C18.7956 10 19.5587 9.68393 20.1213 9.12132C20.6839 8.55871 21 7.79565 21 7C21 6.20435 20.6839 5.44129 20.1213 4.87868C19.5587 4.31607 18.7956 4 18 4Z" fill="#E53935" /></svg>,
                          },
                          {
                            label: "Dropped", value: dropped, status: "dropped",
                            icon: <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" viewBox="0 0 24 24"><path d="M9 4H15V12H19.84L12 19.84L4.16 12H9V4Z" fill="#E53935" /></svg>,
                          },
                          {
                            label: "Deferred", value: deferred, status: "deferred",
                            icon: <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M17.5 12C18.9587 12 20.3576 12.5795 21.3891 13.6109C22.4205 14.6424 23 16.0413 23 17.5C23 18.9587 22.4205 20.3576 21.3891 21.3891C20.3576 22.4205 18.9587 23 17.5 23C16.0413 23 14.6424 22.4205 13.6109 21.3891C12.5795 20.3576 12 18.9587 12 17.5C12 16.0413 12.5795 14.6424 13.6109 13.6109C14.6424 12.5795 16.0413 12 17.5 12ZM15.024 15.024C14.9303 15.1178 14.8776 15.2449 14.8776 15.3775C14.8776 15.5101 14.9303 15.6372 15.024 15.731L16.793 17.501L15.026 19.267C14.9795 19.3134 14.9426 19.3685 14.9174 19.4292C14.8923 19.4899 14.8793 19.5549 14.8792 19.6206C14.8792 19.6863 14.8921 19.7514 14.9172 19.8121C14.9423 19.8728 14.9791 19.928 15.0255 19.9745C15.0719 20.021 15.127 20.0579 15.1877 20.0831C15.2484 20.1082 15.3134 20.1212 15.3791 20.1213C15.4448 20.1213 15.5099 20.1084 15.5706 20.0833C15.6313 20.0582 15.6865 20.0214 15.733 19.975L17.5 18.208L19.27 19.977C19.3161 20.0248 19.3713 20.0628 19.4323 20.0891C19.4933 20.1153 19.5589 20.129 19.6253 20.1296C19.6917 20.1302 19.7575 20.1176 19.819 20.0924C19.8804 20.0673 19.9363 20.0301 19.9832 19.9832C20.0301 19.9363 20.0673 19.8804 20.0924 19.819C20.1176 19.7575 20.1302 19.6917 20.1296 19.6253C20.129 19.5589 20.1153 19.4933 20.0891 19.4323C20.0628 19.3713 20.0248 19.3161 19.977 19.27L18.208 17.5L19.979 15.73C20.0701 15.6357 20.1205 15.5094 20.1193 15.3783C20.1182 15.2472 20.0656 15.1218 19.9729 15.0291C19.8802 14.9364 19.7548 14.8838 19.6237 14.8827C19.4926 14.8815 19.3663 14.9319 19.272 15.023L17.501 16.793L15.731 15.023C15.6372 14.9293 15.5101 14.8766 15.3775 14.8766C15.2449 14.8766 15.1178 14.9293 15.024 15.023M21 8.5L21.001 12.023C19.7529 11.2239 18.2692 10.8752 16.7958 11.0349C15.3224 11.1946 13.9478 11.8529 12.8999 12.9009C11.8519 13.9488 11.1936 15.3234 11.0339 16.7968C10.8742 18.2702 11.2229 19.7539 12.022 21.002L6.25 21C5.38805 21 4.5614 20.6576 3.9519 20.0481C3.34241 19.4386 3 18.612 3 17.75V8.5H21ZM17.75 3C18.612 3 19.4386 3.34241 20.0481 3.9519C20.6576 4.5614 21 5.38805 21 6.25V7H3V6.25C3 5.38805 3.34241 4.5614 3.9519 3.9519C4.5614 3.34241 5.38805 3 6.25 3H17.75Z" fill="#E53935" /></svg>,
                          },
                        ].map((item) => (
                          <div
                            key={item.label}
                            onClick={() => {
                              setSelectedStatusLabel(item.label);
                              setSelectedEmailStatus(item.status);
                              setRefreshTrigger((prev) => prev + 1);
                            }}
                            style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 0", borderBottom: "1px solid #f3f4f6", cursor: "pointer", background: selectedEmailStatus === item.status ? "#fff5f5" : "transparent", borderRadius: 4 }}
                          >
                            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                              {item.icon}
                              <span className="muted">{item.label}</span>
                            </div>
                            <span className="value red">{item.value.toLocaleString()}</span>
                          </div>
                        ))}
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="cardTitleRow">
                        <div className="cardTitle">TOP HOTELS</div>
                        {selectedHotelName && (
                          <div
                            className="badge"
                            style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 4 }}
                            onClick={() => {
                              setSelectedHotelName("");
                              setSelectedEmailStatus("");
                              setSelectedStatusLabel("");
                            }}
                            title="Clear hotel filter"
                          >
                            {selectedHotelName.length > 20
                              ? selectedHotelName.slice(0, 20) + "…"
                              : selectedHotelName}
                            <span style={{ fontSize: 13, lineHeight: 1 }}>×</span>
                          </div>
                        )}
                      </div>
                      <div style={{ flex: 1, minHeight: 120 }}>
                        <AgCharts
                          options={{
                            data: (bcdWidgetCounts?.topHotels ?? []).slice(0, 7),
                            series: [
                              {
                                type: "bar",
                                direction: "horizontal",
                                xKey: "hotelName",
                                yKey: "emailSent",
                                yName: "Email Sent",
                                stacked: true,
                                fill: "#26A69A",
                                tooltip: {
                                  renderer: ({ datum }) => ({
                                    title: datum.hotelName,
                                    content: `Sent: ${datum.emailSent.toLocaleString()}`,
                                  }),
                                },
                                listeners: {
                                  nodeClick: ({ datum }) => {
                                    if (selectedHotelName === datum.hotelName && selectedEmailStatus === "email_sent") {
                                      setSelectedHotelName("");
                                      setSelectedEmailStatus("");
                                      setSelectedStatusLabel("");
                                    } else {
                                      setSelectedHotelName(datum.hotelName);
                                      setSelectedEmailStatus("email_sent");
                                      setSelectedStatusLabel("Sent");
                                    }
                                  },
                                },
                              },
                              {
                                type: "bar",
                                direction: "horizontal",
                                xKey: "hotelName",
                                yKey: "emailNotSent",
                                yName: "Not Sent",
                                stacked: true,
                                fill: "#F87171",
                                tooltip: {
                                  renderer: ({ datum }) => ({
                                    title: datum.hotelName,
                                    content: `Not Sent: ${datum.emailNotSent.toLocaleString()}`,
                                  }),
                                },
                                listeners: {
                                  nodeClick: ({ datum }) => {
                                    if (selectedHotelName === datum.hotelName && selectedEmailStatus === "email_not_sent") {
                                      setSelectedHotelName("");
                                      setSelectedEmailStatus("");
                                      setSelectedStatusLabel("");
                                    } else {
                                      setSelectedHotelName(datum.hotelName);
                                      setSelectedEmailStatus("email_not_sent");
                                      setSelectedStatusLabel("Not Sent");
                                    }
                                  },
                                },
                              },
                            ],
                            axes: [
                              {
                                type: "category",
                                position: "left",
                                label: {
                                  fontSize: 10,
                                  color: "#888",
                                  formatter: (params) =>
                                    params.value?.length > 18
                                      ? params.value.slice(0, 18) + "…"
                                      : params.value,
                                },
                              },
                              {
                                type: "number",
                                position: "bottom",
                                label: {
                                  formatter: (params) =>
                                    Math.round(params.value).toLocaleString(),
                                  fontSize: 10,
                                  color: "#CBD5E0",
                                },
                              },
                            ],
                            legend: { enabled: false },
                            background: { fill: "transparent" },
                            padding: { top: 4, right: 8, bottom: 4, left: 8 },
                          }}
                          style={{ height: 200 }}
                        />
                      </div>
                    </>
                  )}
                </div>
              </>
            )}
          </div>
        ) : (
          /* ===== ORIGINAL UI — all other domains ===== */
          <div className="logsContainer" style={{ display: "flex", gap: 16 }}>
            {loading || !eventData
              ? Array.from({ length: 6 }).map((_, index) => (
                <div
                  key={index}
                  style={{
                    flex: 1,
                    padding: 20,
                    borderRadius: 12,
                    background: "#f9f9f9",
                    minWidth: 150,
                  }}
                >
                  <Skeleton
                    active
                    title={false}
                    paragraph={{ rows: 2, width: ["40%", "60%"] }}
                  />
                </div>
              ))
              : summaryCards.map((item, index) => (
                <StatusCard
                  key={index}
                  label={item.label}
                  value={item.value}
                  icon={item.icon}
                  type={item.type}
                  isSelected={selectedStatusLabel === item.label}
                  onClick={() => {
                    setSelectedStatusLabel(item.label);
                    handleEmailFilter(item.label);
                  }}
                  customStyle={item.customStyle}
                  gst={item.gst}
                />
              ))}
          </div>
        )
      )}


      <div style={{ display: "flex", flex: 1 }}>
        <div
          className="ag-theme-alpine"
          style={{ height: "100%", width: shouldHideUI ? "100%" : "97%" }}
        >
          {clientNamesLoading ? null : <AgTableServer
            ref={agGridRef}
            showPinnedBottomRow={false}
            rowSelection="multiple"
            singleClickEdit={true}
            enableDirectEdit
            key={segmentType}
            endRow={100}
            onFirstDataRendered={handleFirstDataRendered}
            onCellEditingStarted={handleCellEditingStarted}
            onSelectionChanged={handleSelectionChanged}
            //onCellValueChanged={handleCellValueChanged}
            columnDefs={columnDefs}
            onCellValueChange={handleCellValueChange}
            endpoint={hotelFollowUpEndpoint}
            onResponseData={
              isNewUIDomain
                ? (response) => {
                  if (response?.data?.widgetCounts) setBcdWidgetCounts(response.data.widgetCounts);
                  setEventData(response);
                }
                : undefined
            }
            // serverPayload={{
            //   database: "unified-hotel-followup",
            //   table: "booking-data",
            //   selectedClient: selectedClient,
            // }}
            groupSelectsChildren={true}
            autoGroupColumnDef={autoGroupColumnDef}
            onGridReady={handleGridReady}
            serverPayload={serverPayload}
            defaultColDef={DEFAULT_COL_DEF}
            pdfDataKey="invoice_link_new"
            pdfV2
            enablePDFViewer
          />}
        </div>
        {!shouldHideUI && (
          <>
            <Button
              type="default"
              shape="circle"
              icon={open ? <RightOutlined /> : <LeftOutlined />}
              onClick={toggleDrawer}
              style={{
                position: "absolute",
                top: "50%",
                right: open ? "30%" : 0,
                transform: "translateY(-50%)",
                zIndex: 10,
              }}
            />
            <Drawer
              // title="Recent Email Activity"
              placement="right"
              closable={true}
              onClose={toggleDrawer}
              open={open}
              //width={650}
              bodyStyle={{ padding: 16 }}
            >
              <EmailActivityList
                applyFilterToTable={handleBookingIdFilter}
                applyTrackingFilter={handleBookingIdFilterBulk}
              />
            </Drawer>
          </>
        )}

        {/* <div
          style={{
            background: '#ffffff',
            boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.1)',
            borderRadius: '12px',
            padding: '16px',
            marginLeft: '16px',
            marginRight: '16px',
            //marginBottom: '16px',
            flex: 1,
          }}
        >
          <EmailActivityList
            applyFilterToTable={handleBookingIdFilter}
            applyTrackingFilter={handleBookingIdFilterBulk}
          />
        </div> */}
      </div>

      <Modal
        title="Send Bulk Mail"
        open={isBulkMailModalVisible}
        onOk={async () => {
          // Find sender info
          const sender = senderOptions.find(
            (opt) => opt.value === selectedEmail,
          ) || { name: "", email: selectedEmail };

          // Get selected rows from the grid
          const gridApi = agGridRef.current.columnApi.api;
          const selectedRows = gridApi.getSelectedRows();
          // Only include leaf/data rows (with _id or email_bookingId)
          const dataRows = selectedRows.filter(
            (row) => row._id || row.email_bookingId,
          );
          if (dataRows.length === 0) {
            message.warning(
              "Please select at least one row to send bulk mail.",
            );
            return;
          }

          let payload;
          if (sender.value === "Default") {
            // Only send the selected rows and cc (no customText or customFrom)
            payload = {
              customText: customMessage,
              selectedRows: dataRows,
              cc: ccEmails,
            };
          } else {
            // For all other senders, send the full payload as before
            payload = {
              customText: customMessage,
              customFrom: {
                name: sender.name,
                email: sender.value,
              },
              selectedRows: dataRows,
              cc: ccEmails,
            };
          }
          if (Array.isArray(nocFile) && nocFile.length > 0) {
            const base64Files = await Promise.all(
              nocFile.map(async (file) => {
                const base64 = await convertFileToBase64(file);
                return base64; // do not wrap again!
              }),
            );
            payload.nocAttachments = base64Files;
          }

          try {
            const response = await fetch(
              "https://stage-apiplatform.finkraftai.com/api/hotelFollowUp/manualMailTrigger",
              {
                // const response = await fetch(" http://localhost:7003/api/hotelFollowUp/manualMailTrigger", {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                },
                body: JSON.stringify(payload),
              },
            );
            const result = await response.json();
            if (response.ok) {
              message.success(result.message || "Mail sent successfully");
            } else {
              message.error(result.message || "Failed to send mail");
            }
          } catch (error) {
            message.error("Failed to send mail");
          }
          setIsBulkMailModalVisible(false);
          setShowTextarea(false);
          setCustomMessage("");
          setCcEmails([]);
        }}
        onCancel={() => {
          setIsBulkMailModalVisible(false);
          setCcEmails([]);
        }}
        okText="Send"
        cancelText="Cancel"
      >
        {/* Custom "From" dropdown */}
        <div style={{ marginTop: 8 }}>
          <label>
            <b>From:</b>
          </label>
          <Select
            style={{ width: "100%", marginTop: 8 }}
            placeholder="Choose sender email"
            options={emailOptions}
            value={selectedEmail}
            onChange={(value) => setSelectedEmail(value)}
          />
        </div>

        {/* CC multi-select dropdown */}
        <div style={{ marginTop: 16 }}>
          <label>
            <b>CC:</b>
          </label>
          <Select
            mode="multiple"
            allowClear
            style={{ width: "100%", marginTop: 8 }}
            placeholder="Select CC emails"
            options={emailOptions}
            value={ccEmails}
            onChange={setCcEmails}
            optionRender={(option) => (
              <span style={{ display: "flex", alignItems: "center" }}>
                <input
                  type="checkbox"
                  checked={ccEmails.includes(option.value)}
                  readOnly
                  style={{ marginRight: 8 }}
                />
                {option.label}
              </span>
            )}
          />
        </div>

        {/* Add custom message */}
        <div style={{ marginTop: 16 }}>
          {!showTextarea && (
            <Button
              type="link"
              icon={<PlusOutlined />}
              onClick={() => setShowTextarea(true)}
              style={{ color: "#0a8394", paddingLeft: 0 }}
            >
              Add Custom Message
            </Button>
          )}
        </div>

        {showTextarea && (
          <div style={{ marginTop: 8 }}>
            <label>
              <b>Custom Message:</b>
            </label>
            <TextArea
              rows={4}
              placeholder="Write your custom message here..."
              value={customMessage}
              onChange={(e) => setCustomMessage(e.target.value)}
            />
          </div>
        )}
        <div style={{ marginTop: 16 }}>
          <label>
            <b>NOC Attachment:</b>
          </label>
          <input
            type="file"
            accept=".pdf,.jpg,.jpeg,.png"
            multiple
            onChange={(e) => {
              const files = Array.from(e.target.files);
              setNocFile(files);
            }}
          />
          {nocFile && nocFile.length > 0 && (
            <ul style={{ marginTop: 8 }}>
              {nocFile.map((file, index) => (
                <li key={index}>{file.name}</li>
              ))}
            </ul>
          )}
        </div>
      </Modal>

      {/* Single Mail Modal */}
      <Modal
        title="Send Single Mail"
        open={isResendModalVisible}
        onOk={async () => {
          // Find sender info
          const sender = senderOptions.find(
            (opt) => opt.value === selectedEmail,
          ) || { name: "", email: selectedEmail };

          let payload;
          if (sender.value === "Default") {
            // Only send the selected row as payload (plus cc)
            payload = {
              ...resendRowData,
              cc: ccEmailsSingle,
              template: selectedTemplate,
            };
          } else {
            // For all other senders, send the full row data, customText, customFrom, and cc
            payload = {
              ...resendRowData, // all row data
              customText: customMessage,
              customFrom: {
                name: sender.name,
                email: sender.value,
              },
              cc: ccEmailsSingle,
              template: selectedTemplate,
            };
          }

          try {
            const response = await fetch(
              "https://stage-apiplatform.finkraftai.com/api/hotelFollowUp/manualSingleMailTrigger",
              // "http://localhost:7002/api/hotelFollowUp/manualSingleMailTrigger",
              {
                // const response = await fetch("http://localhost:7001/api/hotelFollowUp/manualSingleMailTrigger", {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                },
                body: JSON.stringify(payload),
              },
            );
            const result = await response.json();
            if (response.ok) {
              message.success("Mail triggered successfully");
            } else {
              message.error(result?.message || "Something went wrong");
            }
          } catch (error) {
            message.error("Failed to trigger mail");
          }
          setIsResendModalVisible(false);
          setShowTextarea(false);
          setCustomMessage("");
          setCcEmailsSingle([]);
        }}
        onCancel={() => {
          setIsResendModalVisible(false);
          setCcEmailsSingle([]);
        }}
        okText="Send"
        cancelText="Cancel"
      >
        {/* Multi-checkbox for senders */}
        <div style={{ marginTop: 8 }}>
          <label>
            <b>From:</b>
          </label>
          <Select
            style={{ width: "100%", marginTop: 8 }}
            placeholder="Choose sender email"
            options={senderOptions}
            value={selectedEmail}
            onChange={setSelectedEmail}
          />
        </div>

        {/* CC multi-select dropdown */}
        <div style={{ marginTop: 16 }}>
          <label>
            <b>CC:</b>
          </label>
          <Select
            mode="multiple"
            allowClear
            style={{ width: "100%", marginTop: 8 }}
            placeholder="Select CC emails"
            options={emailOptions}
            value={ccEmailsSingle}
            onChange={setCcEmailsSingle}
            optionRender={(option) => (
              <span style={{ display: "flex", alignItems: "center" }}>
                <input
                  type="checkbox"
                  checked={ccEmailsSingle.includes(option.value)}
                  readOnly
                  style={{ marginRight: 8 }}
                />
                {option.label}
              </span>
            )}
          />
        </div>
        <div style={{ marginTop: 16 }}>
          <label>
            <b>Template:</b>
          </label>

          <Select
            style={{ width: "100%", marginTop: 8 }}
            placeholder="Select Template"
            options={templateOptions}
            value={selectedTemplate}
            onChange={setSelectedTemplate}
          />
        </div>

        {/* Add custom message */}
        <div style={{ marginTop: 16 }}>
          {!showTextarea && (
            <Button
              type="link"
              icon={<PlusOutlined />}
              onClick={() => setShowTextarea(true)}
              style={{ color: "#0a8394", paddingLeft: 0 }}
            >
              Add Custom Message
            </Button>
          )}
        </div>

        {showTextarea && (
          <div style={{ marginTop: 8 }}>
            <label>
              <b>Custom Message:</b>
            </label>
            <TextArea
              rows={4}
              placeholder="Write your custom message here..."
              value={customMessage}
              onChange={(e) => setCustomMessage(e.target.value)}
            />
          </div>
        )}
      </Modal>

      {/* Bulk Single Mail Modal — BCD only */}
      <Modal
        title={`Send Individual Mails (${selectedRowCount} selected)`}
        open={isBulkSingleMailModalVisible}
        confirmLoading={bulkSingleSending}
        okText="Send All"
        cancelText="Cancel"
        onCancel={() => {
          setIsBulkSingleMailModalVisible(false);
          setShowTextarea(false);
          setCustomMessage("");
          setCcEmailsSingle([]);
        }}
        onOk={async () => {
          const gridApi = agGridRef.current?.api;
          if (!gridApi) return;

          const selectedRows = gridApi
            .getSelectedNodes()
            .filter((n) => n.data)
            .map((n) => n.data);

          if (selectedRows.length === 0) {
            message.warning("Please select at least one row.");
            return;
          }

          const sender = senderOptions.find(
            (opt) => opt.value === selectedEmail,
          ) || { name: "", email: selectedEmail };

          setBulkSingleSending(true);

          const results = await Promise.allSettled(
            selectedRows.map((rowData) => {
              const payload =
                sender.value === "Default"
                  ? { ...rowData, cc: ccEmailsSingle, template: selectedTemplate }
                  : {
                    ...rowData,
                    customText: customMessage,
                    customFrom: { name: sender.name, email: sender.value },
                    cc: ccEmailsSingle,
                    template: selectedTemplate,
                  };

              return fetch(
                "https://stage-apiplatform.finkraftai.com/api/hotelFollowUp/manualSingleMailTrigger",
                // "http://localhost:7002/api/hotelFollowUp/manualSingleMailTrigger",
                {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify(payload),
                },
              );
            }),
          );

          setBulkSingleSending(false);

          const failed = results.filter((r) => r.status === "rejected").length;
          const succeeded = results.length - failed;

          if (failed === 0) {
            message.success(
              `${succeeded} mail${succeeded > 1 ? "s" : ""} sent successfully`,
            );
          } else {
            message.warning(`${succeeded} sent, ${failed} failed`);
          }

          setIsBulkSingleMailModalVisible(false);
          setShowTextarea(false);
          setCustomMessage("");
          setCcEmailsSingle([]);
        }}
      >
        <div style={{ marginTop: 8 }}>
          <label><b>From:</b></label>
          <Select
            style={{ width: "100%", marginTop: 8 }}
            placeholder="Choose sender email"
            options={senderOptions}
            value={selectedEmail}
            onChange={setSelectedEmail}
          />
        </div>

        <div style={{ marginTop: 16 }}>
          <label><b>CC:</b></label>
          <Select
            mode="multiple"
            allowClear
            style={{ width: "100%", marginTop: 8 }}
            placeholder="Select CC emails"
            options={emailOptions}
            value={ccEmailsSingle}
            onChange={setCcEmailsSingle}
            optionRender={(option) => (
              <span style={{ display: "flex", alignItems: "center" }}>
                <input
                  type="checkbox"
                  checked={ccEmailsSingle.includes(option.value)}
                  readOnly
                  style={{ marginRight: 8 }}
                />
                {option.label}
              </span>
            )}
          />
        </div>
        <div style={{ marginTop: 16 }}>
          <label>
            <b>Template:</b>
          </label>

          <Select
            style={{ width: "100%", marginTop: 8 }}
            placeholder="Select Template"
            options={templateOptions}
            value={selectedTemplate}
            onChange={setSelectedTemplate}
          />
        </div>

        <div style={{ marginTop: 16 }}>
          {!showTextarea ? (
            <Button
              type="link"
              icon={<PlusOutlined />}
              onClick={() => setShowTextarea(true)}
              style={{ color: "#0a8394", paddingLeft: 0 }}
            >
              Add Custom Message
            </Button>
          ) : (
            <div>
              <label><b>Custom Message:</b></label>
              <TextArea
                rows={4}
                style={{ marginTop: 8 }}
                placeholder="Write your custom message here..."
                value={customMessage}
                onChange={(e) => setCustomMessage(e.target.value)}
              />
            </div>
          )}
        </div>

        <div
          style={{
            marginTop: 16,
            padding: "10px 12px",
            background: "#f5fafa",
            border: "1px solid #e0f2f2",
            borderRadius: 4,
            fontSize: 12,
            color: "#555",
          }}
        >
          This will send{" "}
          <b>
            {selectedRowCount} individual email
            {selectedRowCount !== 1 ? "s" : ""}
          </b>
          , one per selected row.
        </div>
      </Modal>

      <Modal
        title="Template Details"
        open={isModalVisible}
        onOk={handleOk}
        onCancel={handleCancel}
        width={800} // This sets the width to 800px
        footer={false}
        style={{ width: 800 }} // This is redundant if width is already set
      >
        {/* {templateData && (
          <div
            style={{
              height: 500,
              width: "100%",
              marginTop: 24,
              overflowY: "auto",
            }}
            dangerouslySetInnerHTML={{
              __html: renderTemplate(templateData, modalData),
            }}
          />
        )} */}
        {modalData?.booking_client_id && (() => {
          const key = `${modalData.booking_client_id}_${modalData.selectedTemplateId}`;
          const templateInfo = clientTemplateHtmlMapRef.current[key];
          if (!templateInfo) return null;

          const DEMO_GENERIC_HOTEL_INTERVALS = [
            "BOOKING_HOTEL",
            "CHECKIN_HOTEL",
            "BEFORE_CHECKOUT_HOTEL",
            "AFTER_CHECKOUT_HOTEL",
            "COMMON_TEMPLATE",
          ];
          const isDemoWorkspace = isBcd && workspaceName?.toLowerCase() === "demo";
          const shouldUseGenericHotelName =
            isDemoWorkspace &&
            DEMO_GENERIC_HOTEL_INTERVALS.includes(modalData.selectedInterval);

          const maskConfirmationNumber = (value) =>
            value ? `${String(value).slice(0, 2)}*******` : value;

          const templateModalData = isDemoWorkspace
            ? {
              ...modalData,
              ...(shouldUseGenericHotelName
                ? {
                  email_hotel_name: "HOTEL",
                  hotel_name: "HOTEL",
                  hotel_details: {
                    ...(modalData.hotel_details || {}),
                    hotel_name: "HOTEL",
                  },
                }
                : {}),
              email_bookingId: maskConfirmationNumber(modalData.email_bookingId),
              email_sharableBookingId: maskConfirmationNumber(modalData.email_sharableBookingId),
              confirmation_number: maskConfirmationNumber(modalData.confirmation_number),
              booking_details: {
                ...(modalData.booking_details || {}),
                confirmation_number: maskConfirmationNumber(
                  modalData.booking_details?.confirmation_number ??
                  modalData.email_bookingId
                ),
              },
              company_name: maskConfirmationNumber(modalData.company_name),
              gstin: maskConfirmationNumber(modalData.gstin),
              company_address: maskConfirmationNumber(modalData.company_address),
              entity: {
                ...(modalData.entity || {}),
                name: maskConfirmationNumber(modalData.entity?.name ?? modalData.company_name),
                gstin: maskConfirmationNumber(modalData.entity?.gstin ?? modalData.gstin),
                address: maskConfirmationNumber(modalData.entity?.address ?? modalData.company_address),
              },
            }
            : modalData;

          return (
            <div
              style={{ height: 500, width: "100%", marginTop: 24, overflowY: "auto" }}
              dangerouslySetInnerHTML={{
                __html: renderTemplate(templateInfo.html_content, {
                  ...templateModalData,
                  topBookings: [templateModalData],
                  booking_activity: BCD_ACTIVITY_TYPE[modalData.selectedInterval] || "",
                  partner1_logo: clientNamesMapRef.current[modalData.booking_client_id]?.partner1_logo || "",
                  partner2_logo: clientNamesMapRef.current[modalData.booking_client_id]?.partner2_logo || "",
                }),
              }}
            />
          );
        })()}
      </Modal>

      {modalConfig.visible && (
        <Modal
          title={
            modalConfig.type === "email"
              ? "Update Hotel Contact Email"
              : "Update Hotel Contact Number"
          }
          open={modalConfig.visible}
          onCancel={() => setModalConfig({ visible: false })}
          footer={false}
          width={700}
        >
          <HotelDataUpdate
            contactDetails={contactDetails}
            onSuccess={() => setModalConfig({ visible: false })}
            type={modalConfig.type}
            segmentType={segmentType}
            onDataRefresh={refreshGridData}
            onRowDataUpdate={updateRowData}
          />
        </Modal>
      )}

      <Modal
        title="Customer Info"
        open={isCustomerInfoModalVisible}
        onCancel={() => setIsCustomerInfoModalVisible(false)}
        footer={null}
        width={1100}
        destroyOnClose
        styles={{ body: { height: "65vh", overflow: "visible" } }}
      >
        <div
          id="customer-info-grid-wrapper"
          style={{ height: "100%", width: "100%", position: "relative" }}
          ref={(el) => {
            if (el) window.__customerInfoGridWrapper = el;
          }}
        >
          <AgTableClient
            groupPanel
            rowData={customerInfoData}
            columnDefs={CUSTOMER_INFO_COLUMN_DEFS}
            hideToolbar
            popupParent={
              typeof window !== "undefined"
                ? document.getElementById("customer-info-grid-wrapper") || document.body
                : document.body
            }
          />
        </div>
      </Modal>
    </div>
  );
};

export default HotelEmail;

// Named test exports to improve unit coverage on pure helpers
export const __test__ = {
  // format INR values with Crore/Lakh logic
  formatAmountIndian,
  // base64 converter used for NOC attachments
  convertFileToBase64,
};
